// State-level SEO content for /youth-soccer-forum-usa/[state]-youth-soccer pages.
// Keyed by category.slug. No DB queries — bundled at build time.

export type StateGuide = {
  state: string;
  abbreviation: string;
  heroTagline: string;
  leagues: { name: string; description: string }[];
  tournaments: { name: string; description: string }[];
  tryoutWindows: string;
  collegePipeline: string;
  faqs: { question: string; answer: string }[];
  forumLinks: { label: string; to: string }[];
};

export const stateGuides: Record<string, StateGuide> = {
  "new-jersey-youth-soccer": {
    state: "New Jersey",
    abbreviation: "NJ",
    heroTagline:
      "Talking Soccer NJ — your hub for New Jersey youth soccer. Clubs, tryouts, tournaments and parent talk from North Jersey to the Shore.",
    leagues: [
      { name: "EDP League (Elite Development Program)", description: "Heavy NJ presence U9–U19. Most travel clubs from Bergen down to Monmouth field EDP teams." },
      { name: "MAPS (Mid-Atlantic Premiership Soccer)", description: "Top-tier travel league connecting NJ clubs with PA/NY/DE for stronger weekly competition." },
      { name: "ECNL & ECNL Regional League — New Jersey", description: "PDA, Cedar Stars, Match Fit, and Sky Blue–PDA pull recruiters here every weekend." },
      { name: "MLS Next — New Jersey clubs", description: "NY Red Bulls, PDA, Cedar Stars Academy and Black Rock anchor the boys' MLS Next pathway." },
      { name: "NJYS State Cup", description: "Annual state championship feeding into US Youth Soccer Eastern Regionals." },
    ],
    tournaments: [
      { name: "Jefferson Cup qualifier weekends", description: "NJ clubs use fall showcases (MAPS Cup, EDP Columbus Day) as tune-ups before Jeff Cup in March." },
      { name: "PDA Summer & Winter Showcases", description: "Two of the most-attended college recruiting events on the East Coast." },
      { name: "Cedar Stars Cup", description: "Bergen-based showcase that has grown into a national draw for ECNL/MLS Next age groups." },
      { name: "Manhattan Kickoff & Surf Cup East", description: "Popular early-season targets for top NJ travel teams." },
    ],
    tryoutWindows:
      "Most NJ travel/ECNL/MLS Next clubs hold tryouts in late May through June for the following season. Pre-tryout clinics run April–May. Recreational/town programs typically register in August.",
    collegePipeline:
      "NJ consistently sends players to Big Ten, ACC, Big East and Patriot League programs. PDA, Cedar Stars, Match Fit and Players Development Academy alumni feature heavily on Division I rosters.",
    faqs: [
      { question: "When are NJ youth soccer tryouts?", answer: "Most New Jersey travel, ECNL and MLS Next clubs hold tryouts in late May and June for the following season, with pre-tryout clinics running April–May. Town recreational programs typically register in August." },
      { question: "What are the best ECNL clubs in New Jersey?", answer: "PDA (Players Development Academy), Cedar Stars Academy, Match Fit Academy and Sky Blue–PDA are the most consistently-recruited ECNL programs in NJ." },
      { question: "EDP vs MAPS vs MLS Next in NJ — what's the difference?", answer: "EDP is the broad travel league most NJ clubs play in. MAPS is a higher-level regional league for top teams. MLS Next is the elite boys' national pathway run by MLS, and ECNL is the equivalent elite girls' and boys' national league." },
      { question: "Which NJ club has the best MLS Next program?", answer: "NY Red Bulls Academy, PDA and Cedar Stars Academy anchor the boys' MLS Next pathway in New Jersey, with Black Rock also fielding strong age groups." },
      { question: "What's the path to NJ State Cup?", answer: "NJ Youth Soccer (NJYS) runs the State Cup each spring. Winners advance to US Youth Soccer Eastern Regionals and potentially to National Championships." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
      { label: "Tryouts", to: "/club-and-travel-soccer/tryouts" },
    ],
  },

  "florida-youth-soccer": {
    state: "Florida",
    abbreviation: "FL",
    heroTagline:
      "Talking Soccer Florida — from South Florida academies to Tampa Bay and Jacksonville. Clubs, tryouts, showcases and recruiting in one place.",
    leagues: [
      { name: "Florida State Premier League (FSPL)", description: "Top in-state travel platform, U9–U19, with promotion/relegation across divisions." },
      { name: "ECNL Florida & ECNL Regional League", description: "Weston FC, Tampa Bay United, Florida Premier, IMG Academy and Jacksonville FC anchor weekly play." },
      { name: "MLS Next — Florida clubs", description: "Inter Miami CF Academy, Orlando City, Weston FC, IMG and Tampa Bay United lead the boys' MLS Next pathway." },
      { name: "Girls Academy League (GA) — Florida", description: "Strong South Florida and Central Florida footprint at top age groups." },
      { name: "FYSA State Cup", description: "Path to US Youth Soccer Southern Regionals — biggest in-state competition outside national leagues." },
    ],
    tournaments: [
      { name: "Disney Soccer Showcase (Orlando)", description: "Massive holiday-week showcase at ESPN Wide World of Sports — heavy college recruiter attendance." },
      { name: "IMG Academy Cup (Bradenton)", description: "Premier early-season event drawing national MLS Next and ECNL clubs." },
      { name: "Florida Premier Thanksgiving Invitational", description: "Top Tampa-area showcase for ECNL and elite travel age groups." },
      { name: "Sunshine State Cup & Weston Cup", description: "Long-running South Florida tournaments with strong international draw." },
    ],
    tryoutWindows:
      "Florida clubs typically run tryouts in late May–early June. ECNL and MLS Next programs often start ID sessions in April. Year-round weather lets many clubs run pre-tryout academy weeks.",
    collegePipeline:
      "Florida produces a steady flow of D1 commits to ACC, SEC and AAC programs. IMG Academy, Weston FC, Tampa Bay United and Orlando City consistently rank among the most-recruited clubs in the Southeast.",
    faqs: [
      { question: "When are Florida soccer tryouts?", answer: "Most Florida travel and ECNL/MLS Next clubs run tryouts in late May to early June. Many programs hold ID sessions and pre-tryout academy weeks starting in April." },
      { question: "What are the best youth soccer clubs in Florida?", answer: "Inter Miami CF Academy, IMG Academy, Weston FC, Orlando City, Tampa Bay United, Florida Premier and Jacksonville FC are consistently among the most-recruited Florida clubs." },
      { question: "Is FSPL or ECNL bigger in Florida?", answer: "ECNL is the higher-level national league, but FSPL (Florida State Premier League) is the most widely-played in-state travel platform and is where most Florida clubs compete weekly." },
      { question: "How does FYSA State Cup work?", answer: "Florida Youth Soccer Association runs the State Cup each year. Winners advance to US Youth Soccer Southern Regionals, with a path to the National Championships." },
      { question: "What's the biggest Florida youth soccer tournament?", answer: "The Disney Soccer Showcase at ESPN Wide World of Sports in Orlando is the largest and most college-attended event held in Florida each year." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
      { label: "Tryouts", to: "/club-and-travel-soccer/tryouts" },
    ],
  },

  "pennsylvania-youth-soccer": {
    state: "Pennsylvania",
    abbreviation: "PA",
    heroTagline:
      "Talking Soccer PA — Pennsylvania youth soccer from Philly's club scene to Pittsburgh, Lehigh Valley and Central PA travel and ECNL.",
    leagues: [
      { name: "EPYSA & State Cup", description: "Eastern Pennsylvania Youth Soccer runs the State Cup and feeds US Youth Soccer Eastern Regionals." },
      { name: "ECNL Mid-Atlantic", description: "FC Bucks, YSC Academy, Penn Fusion and Philadelphia SC anchor the girls' ECNL pathway." },
      { name: "MLS Next — Philadelphia Union & Pittsburgh Riverhounds", description: "Philadelphia Union Academy is one of the strongest MLS Next pipelines in the country; YSC Academy is a key partner." },
      { name: "EDP & MAPS", description: "Most southeast PA clubs play EDP/MAPS for stronger weekly competition than local-only leagues." },
      { name: "PA West Soccer", description: "Governs travel soccer for the western half of the state including the Pittsburgh metro." },
    ],
    tournaments: [
      { name: "Bethesda Premier Cup", description: "Major Mid-Atlantic showcase that draws top PA clubs every fall and spring." },
      { name: "Philadelphia Union Cup", description: "Run by the Union Academy at YSC Sports — competitive ECNL/MLS Next field." },
      { name: "Jefferson Cup (Richmond)", description: "Most top PA travel teams target this March event; heavy recruiter attendance." },
      { name: "PA Classics College Showcase", description: "Long-running Lancaster-based event with strong D1/D2/D3 college coach turnout." },
    ],
    tryoutWindows: "EPYSA and most PA travel clubs run tryouts in late May–June for the following year. ECNL and MLS Next clubs often hold ID sessions in April–May. Recreational and inter-town soccer registers in August.",
    collegePipeline: "Philadelphia Union Academy alumni are regularly homegrown into the MLS first team. PA clubs feed Penn State, Pitt, Villanova, Drexel and Patriot/Ivy League programs heavily.",
    faqs: [
      { question: "When are PA soccer tryouts?", answer: "Most Pennsylvania travel clubs hold tryouts in late May and June. ECNL and MLS Next clubs typically run ID sessions starting in April. Recreational and inter-town leagues register in August." },
      { question: "How do I join Philadelphia Union Academy?", answer: "Philadelphia Union Academy recruits year-round through scouting and open ID sessions tied to YSC Academy. The pathway is highly competitive — most Academy players come through MLS Next clubs already affiliated with the Union." },
      { question: "What's the difference between EPYSA and PA West Soccer?", answer: "EPYSA covers the eastern half of Pennsylvania (Philadelphia metro, Lehigh Valley, Central PA). PA West Soccer covers the western half including Pittsburgh. Each runs its own State Cup." },
      { question: "Best ECNL clubs in Pennsylvania?", answer: "FC Bucks, YSC Academy, Penn Fusion, Philadelphia SC and Pittsburgh Riverhounds Development Academy are the most consistently-recruited ECNL programs in PA." },
      { question: "What's the biggest PA youth soccer showcase?", answer: "Bethesda Premier Cup and the PA Classics College Showcase are the largest recruiting events PA clubs attend regularly, with Jefferson Cup in March as the biggest national draw." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "massachusetts-youth-soccer": {
    state: "Massachusetts",
    abbreviation: "MA",
    heroTagline:
      "Talking Soccer MA — Massachusetts youth soccer talk: NEFC, FC Stars, NEP, Valeo and the Greater Boston club and tryout scene.",
    leagues: [
      { name: "Mass Youth Soccer & State Cup", description: "Governing body for travel and inter-town across the Commonwealth; State Cup feeds Eastern Regionals." },
      { name: "ECNL New England", description: "NEFC, FC Stars, Valeo, Seacoast United and Scorpions SC make MA an ECNL stronghold for both girls and boys." },
      { name: "MLS Next — New England", description: "NEFC and New England Revolution Academy anchor the boys' MLS Next pathway in MA." },
      { name: "NEP (New England Premiership)", description: "Top regional travel league connecting MA, NH, ME, RI and CT clubs." },
      { name: "BAYS / MAPLE", description: "Long-standing Boston-area youth and inter-town leagues feeding the travel system." },
    ],
    tournaments: [
      { name: "NEFC Showcase (Devens)", description: "Major recruiting weekend on the East Coast — ECNL, MLS Next and elite travel divisions." },
      { name: "FC Stars Cup", description: "Boston-area showcase with college recruiter attendance at older age groups." },
      { name: "Columbus Day & Memorial Day Tournaments", description: "Most New England clubs anchor their season around these long-weekend events." },
    ],
    tryoutWindows: "MA travel clubs typically run tryouts in late May through June. ECNL and MLS Next programs often start ID clinics in April. Recreational programs register in August.",
    collegePipeline: "Massachusetts produces a steady stream of Patriot, Ivy, ACC and Big East commits. NEFC, FC Stars and Valeo alumni feature heavily on D1 rosters across the Northeast.",
    faqs: [
      { question: "When are Massachusetts soccer tryouts?", answer: "Most MA travel and ECNL clubs run tryouts in late May through June. Many programs offer ID clinics starting in April." },
      { question: "Best ECNL clubs in Massachusetts?", answer: "NEFC, FC Stars, Valeo, Seacoast United and Scorpions SC are the most consistently-recruited ECNL programs in Massachusetts." },
      { question: "What is NEP soccer?", answer: "NEP (New England Premiership) is a top regional travel league that connects clubs across MA, NH, ME, RI and CT — a level above most state-only competition." },
      { question: "How does New England Revolution Academy recruit?", answer: "The Revs Academy scouts through MLS Next play, regional tournaments and open trials. NEFC partners closely with the Academy for MLS Next development." },
      { question: "What's the path to Mass Youth State Cup?", answer: "Teams qualify through their league standings and register through Mass Youth Soccer. Winners advance to US Youth Soccer Eastern Regionals." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "washington-youth-soccer": {
    state: "Washington",
    abbreviation: "WA",
    heroTagline:
      "Talking Soccer WA — Washington youth soccer: Crossfire, Eastside FC, WPFC, Seattle United and Sounders Academy from Puget Sound to Spokane.",
    leagues: [
      { name: "Washington Youth Soccer & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Far West Regionals." },
      { name: "RCL (Regional Club League)", description: "Premier in-state travel league with promotion/relegation — the proving ground for top WA clubs." },
      { name: "ECNL Northwest", description: "Crossfire Premier, Washington Premier, Eastside FC and Pacific FC anchor the ECNL girls' pathway." },
      { name: "MLS Next — Seattle Sounders Academy & Crossfire", description: "Seattle Sounders Academy is the flagship MLS Next program in the region." },
      { name: "ECNL Regional League — Northwest", description: "Second-tier ECNL competition for fast-rising WA clubs." },
    ],
    tournaments: [
      { name: "Crossfire Challenge", description: "One of the largest tournaments in the Pacific Northwest — strong out-of-state draw." },
      { name: "Surf Cup College Showcase (San Diego)", description: "Top WA travel teams routinely travel for Surf Cup and Far West Regionals." },
      { name: "WPFC Spring Classic", description: "Puyallup-based showcase popular with regional ECNL and MLS Next clubs." },
    ],
    tryoutWindows: "Most WA travel and ECNL clubs hold tryouts in late May–June for the following season. Sounders Academy and MLS Next ID sessions run year-round.",
    collegePipeline: "Washington clubs feed UW, Gonzaga, Seattle U and Pac-12/WCC programs, plus a strong flow to California and East Coast D1 schools.",
    faqs: [
      { question: "When are Washington soccer tryouts?", answer: "Most WA travel and ECNL clubs run tryouts in late May and June. Seattle Sounders Academy and other MLS Next programs hold ID sessions year-round." },
      { question: "What is RCL soccer in Washington?", answer: "RCL (Regional Club League) is the top in-state travel league in Washington, with promotion and relegation across divisions. Many top WA clubs use RCL as their weekly competition." },
      { question: "Best youth soccer clubs in Washington?", answer: "Crossfire Premier, Seattle Sounders Academy, Washington Premier FC, Eastside FC, Seattle United and Pacific FC are the most consistently-recruited clubs in Washington." },
      { question: "How do I get into Sounders Academy?", answer: "Sounders Academy recruits through MLS Next play, regional ID sessions and partnerships with affiliate clubs. Open tryouts are limited — most players come through scouted MLS Next competition." },
      { question: "What is the biggest WA soccer tournament?", answer: "The Crossfire Challenge is one of the largest and most attended tournaments in the Pacific Northwest each year." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "oregon-youth-soccer": {
    state: "Oregon",
    abbreviation: "OR",
    heroTagline:
      "Talking Soccer OR — Oregon youth soccer: Portland Timbers Academy, FC Portland, Westside Timbers and Eastside clubs from Portland to Bend.",
    leagues: [
      { name: "Oregon Youth Soccer & State Cup", description: "Governing body and pathway to US Youth Soccer Far West Regionals." },
      { name: "OPL (Oregon Premier League)", description: "Top in-state travel league for U13–U19." },
      { name: "ECNL Northwest — Oregon clubs", description: "FC Portland and Westside Timbers anchor the ECNL girls' pathway in Oregon." },
      { name: "MLS Next — Portland Timbers Academy", description: "Flagship boys' development pathway in the Pacific Northwest." },
      { name: "RCL crossover", description: "Top OR teams often play into Washington's RCL for tougher competition." },
    ],
    tournaments: [
      { name: "Portland Timbers Cup", description: "Major regional showcase hosted by the Timbers organization." },
      { name: "Westside Timbers Memorial Day Classic", description: "Heavy attendance from Pacific Northwest clubs each spring." },
      { name: "Far West Regionals", description: "End-of-season target for State Cup winners across OR." },
    ],
    tryoutWindows: "OR travel and ECNL clubs hold tryouts in late May–June. Timbers Academy and MLS Next programs run ID sessions throughout the year.",
    collegePipeline: "Oregon clubs feed Oregon, Oregon State, Portland (a longtime women's power) and Pac-12/WCC programs.",
    faqs: [
      { question: "When are Oregon soccer tryouts?", answer: "Most Oregon travel and ECNL clubs hold tryouts in late May and June. Portland Timbers Academy runs ID sessions year-round through MLS Next." },
      { question: "Best youth soccer clubs in Oregon?", answer: "Portland Timbers Academy, FC Portland and Westside Timbers are the most consistently-recruited Oregon clubs." },
      { question: "How do I join Portland Timbers Academy?", answer: "Timbers Academy scouts year-round through MLS Next play, regional ID camps and affiliate club partnerships. Open tryouts are rare." },
      { question: "What is OPL (Oregon Premier League)?", answer: "OPL is the top in-state travel league for U13–U19. It's where most competitive Oregon teams play their weekly league games." },
      { question: "Where do Oregon players go for college?", answer: "Oregon clubs commonly feed University of Oregon, Oregon State, University of Portland (a longtime women's power), and other Pac-12 and WCC programs." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "connecticut-youth-soccer": {
    state: "Connecticut",
    abbreviation: "CT",
    heroTagline:
      "Talking Soccer CT — Connecticut youth soccer: Beachside, Oakwood, CFC, FSA and the Fairfield-to-Hartford club scene.",
    leagues: [
      { name: "Connecticut Junior Soccer Association & State Cup", description: "Governing body and path to US Youth Soccer Eastern Regionals." },
      { name: "ECNL New England — CT clubs", description: "Beachside SC, Oakwood SC, CFC and FSA are anchor ECNL clubs in the state." },
      { name: "MLS Next — Connecticut", description: "Oakwood and Beachside lead the MLS Next pathway for boys in CT." },
      { name: "NPL & EDP Connecticut", description: "Top travel platforms for clubs not in ECNL/MLS Next." },
    ],
    tournaments: [
      { name: "Beachside Showcase", description: "Major Stamford-area event drawing top ECNL clubs from across the Northeast." },
      { name: "Oakwood Soccer Cup", description: "Long-running CT showcase popular with D1/D2 college recruiters." },
      { name: "CT Cup & New England Regionals", description: "End-of-season targets for State Cup winners." },
    ],
    tryoutWindows: "CT travel and ECNL clubs run tryouts in late May–June. ID clinics typically start in April. Town recreational programs register in August.",
    collegePipeline: "Connecticut consistently sends players to UConn, Yale, Quinnipiac, Fairfield and Ivy/Patriot League programs.",
    faqs: [
      { question: "When are Connecticut soccer tryouts?", answer: "Most CT travel and ECNL clubs hold tryouts in late May and June. ID clinics typically start in April. Town recreational programs register in August." },
      { question: "Best ECNL clubs in Connecticut?", answer: "Beachside SC, Oakwood SC, CFC and FSA are the four anchor ECNL clubs in Connecticut and the most consistently-recruited." },
      { question: "What's the difference between ECNL and NPL in CT?", answer: "ECNL is a national-level elite league. NPL (National Premier League) is a tier below — strong competition for clubs not currently in ECNL or MLS Next." },
      { question: "Is Beachside a good club?", answer: "Yes — Beachside SC is one of the most consistently top-recruited ECNL clubs in New England, with strong placement into D1 programs." },
      { question: "How does CT State Cup work?", answer: "CJSA (Connecticut Junior Soccer Association) runs State Cup each spring. Winners advance to New England Regionals and then potentially Eastern Regionals and Nationals." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "texas-youth-soccer": {
    state: "Texas",
    abbreviation: "TX",
    heroTagline:
      "Talking Soccer TX — Texas youth soccer: Dallas, Houston, Austin and San Antonio clubs, FC Dallas Academy, Solar, Lonestar and Albion.",
    leagues: [
      { name: "North Texas State Soccer & STX (South Texas) Soccer", description: "Two of the largest state youth soccer associations in the country — separate governance for North and South TX." },
      { name: "ECNL Texas / ECNL Southwest", description: "Solar SC, Dallas Texans, Lonestar SC, Albion Hurricanes and Houston Dash YS anchor ECNL." },
      { name: "MLS Next — FC Dallas Academy & Houston Dynamo Academy", description: "Two of the top boys' MLS Next pathways in the country — FC Dallas in particular is a national model." },
      { name: "Girls Academy League (GA) — Texas", description: "Strong North Texas footprint at top age groups." },
      { name: "USYS National League Texas", description: "Alternate national-level pathway for top Texas teams." },
    ],
    tournaments: [
      { name: "Dallas Cup", description: "One of the most prestigious youth tournaments in the world — hosted at the Cotton Bowl complex." },
      { name: "Lonestar Sting Round Robin (Austin)", description: "Top early-season target for ECNL and MLS Next clubs." },
      { name: "Houston Texans Cup & STX President's Cup", description: "Major in-state events for Houston and South Texas clubs." },
    ],
    tryoutWindows: "Texas clubs run tryouts in late May–June; many also offer winter ID/skills clinics. MLS Next programs at FC Dallas and Houston Dynamo recruit year-round.",
    collegePipeline: "Texas produces a massive flow of D1 commits to Big 12, SEC and ACC programs. FC Dallas, Solar and Dallas Texans are consistently among the most-recruited clubs in the country.",
    faqs: [
      { question: "When are Texas soccer tryouts?", answer: "Most Texas travel and ECNL clubs hold tryouts in late May and June. FC Dallas, Houston Dynamo and other MLS Next academies recruit year-round." },
      { question: "Best youth soccer clubs in Texas?", answer: "FC Dallas Academy, Houston Dynamo Academy, Solar SC, Dallas Texans, Lonestar SC and Albion Hurricanes are the most consistently top-recruited clubs in Texas." },
      { question: "How do I join FC Dallas Academy?", answer: "FC Dallas Academy scouts year-round through MLS Next play, regional ID camps and affiliate club partnerships. Open tryouts are very limited — most players are identified through scouted competition." },
      { question: "What is the Dallas Cup?", answer: "The Dallas Cup is one of the most prestigious youth soccer tournaments in the world, hosted at the Cotton Bowl complex each year. Top international academies and elite US clubs compete." },
      { question: "North Texas vs South Texas Soccer Association?", answer: "Texas is split into two state associations: North Texas State Soccer (Dallas/Fort Worth and the northern half) and South Texas Youth Soccer (Houston, Austin, San Antonio and the south). Each runs its own State Cup." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "california-youth-soccer": {
    state: "California",
    abbreviation: "CA",
    heroTagline:
      "Talking Soccer CA — California youth soccer: LA Galaxy, LAFC, Surf, Strikers, Slammers, De Anza Force, MVLA and the deepest club scene in the country.",
    leagues: [
      { name: "Cal South & Cal North", description: "California is split into two state associations — among the largest youth soccer governing bodies in the US." },
      { name: "ECNL Southwest & ECNL Northwest", description: "Surf SC, Slammers FC, Strikers FC, Beach FC, MVLA, De Anza Force and Marin FC anchor ECNL in California." },
      { name: "MLS Next — LA Galaxy Academy, LAFC Academy, Earthquakes, San Diego FC", description: "California has more MLS Next academies than any other state." },
      { name: "Girls Academy League (GA) — California", description: "Strong SoCal footprint at top age groups." },
      { name: "SCDSL & NorCal Premier", description: "Top tier in-state travel leagues outside of the national pathways." },
    ],
    tournaments: [
      { name: "Surf Cup (San Diego)", description: "One of the most prestigious club soccer tournaments in the country — heavy college recruiter attendance." },
      { name: "Manchester City Cup & Players College Showcase", description: "Major SoCal events drawing national clubs every year." },
      { name: "Davis Legacy & NorCal State Cup", description: "Top targets for Northern California clubs." },
    ],
    tryoutWindows: "California clubs typically run tryouts in May–June. SoCal ECNL and MLS Next programs often start ID sessions in April. Year-round weather lets many clubs run continuous academy training.",
    collegePipeline: "California produces more D1 college soccer commits than any other state. Surf, Slammers, Strikers, MVLA and De Anza Force routinely top recruiting class rankings.",
    faqs: [
      { question: "When are California soccer tryouts?", answer: "Most California travel and ECNL clubs hold tryouts in May and June. SoCal MLS Next academies often start ID sessions in April, with year-round training given the weather." },
      { question: "Best youth soccer clubs in California?", answer: "Surf SC, Slammers FC, Strikers FC, Beach FC, LA Galaxy Academy, LAFC Academy, MVLA and De Anza Force are among the most consistently top-recruited clubs in California." },
      { question: "What's the difference between Cal South and Cal North?", answer: "California is governed by two state associations: Cal South (Southern California) and Cal North (Northern California). Each runs its own State Cup and league competition." },
      { question: "How prestigious is Surf Cup?", answer: "Surf Cup in San Diego is one of the most prestigious youth soccer tournaments in the country, with very heavy college recruiter attendance at older age groups." },
      { question: "How do I join LA Galaxy or LAFC Academy?", answer: "Both MLS academies scout year-round through MLS Next play, regional ID camps and partnerships with affiliate clubs. Open tryouts are rare — most players are identified through scouted competition." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "north-carolina-youth-soccer": {
    state: "North Carolina",
    abbreviation: "NC",
    heroTagline:
      "Talking Soccer NC — North Carolina youth soccer: NCFC, CASL, Charlotte SA, Discoveries, TFCA and the Triangle-to-Charlotte club pipeline.",
    leagues: [
      { name: "NCYSA & State Cup", description: "Governing body and pathway to US Youth Soccer Southern Regionals." },
      { name: "ECNL North Carolina & ECNL Regional League", description: "NCFC Youth, Charlotte SA, TFCA and CESA cross-border anchor ECNL in the Carolinas." },
      { name: "MLS Next — NCFC Academy & Charlotte FC Academy", description: "Both academies are growing into top southeastern boys' pathways." },
      { name: "Girls Academy League (GA) — NC", description: "Solid Triangle and Charlotte footprint at older age groups." },
      { name: "Classic League / NCYSA Premiership", description: "In-state competition for travel teams outside ECNL/MLS Next." },
    ],
    tournaments: [
      { name: "Jefferson Cup qualifier weekends", description: "NC clubs use fall showcases as tune-ups before Jeff Cup (Richmond) in March." },
      { name: "CASL Showcase & NCFC Showcase", description: "Major Triangle-area recruiting weekends." },
      { name: "Discoveries Cup", description: "Long-running Charlotte-area showcase." },
    ],
    tryoutWindows: "NC travel and ECNL clubs typically run tryouts in late May–June. Charlotte FC and NCFC Academy run year-round ID sessions for MLS Next.",
    collegePipeline: "NC consistently sends players to UNC (a longtime women's power), Duke, NC State, Wake Forest and the broader ACC/SEC.",
    faqs: [
      { question: "When are North Carolina soccer tryouts?", answer: "Most NC travel and ECNL clubs hold tryouts in late May and June. Charlotte FC Academy and NCFC Academy run year-round MLS Next ID sessions." },
      { question: "Best youth soccer clubs in NC?", answer: "NCFC Youth, Charlotte SA, TFCA (Triangle FC Alliance) and Discoveries SC are the most consistently top-recruited clubs in North Carolina." },
      { question: "How do I join Charlotte FC Academy?", answer: "Charlotte FC Academy scouts through MLS Next play and regional ID camps year-round. Most Academy players come through scouted competition rather than open tryouts." },
      { question: "What's the path to NC State Cup?", answer: "NCYSA (North Carolina Youth Soccer Association) runs the State Cup each year. Winners advance to US Youth Soccer Southern Regionals." },
      { question: "Where do NC players go for college?", answer: "North Carolina players regularly commit to UNC (a longtime women's power), Duke, NC State, Wake Forest and the broader ACC and SEC." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "georgia-youth-soccer": {
    state: "Georgia",
    abbreviation: "GA",
    heroTagline:
      "Talking Soccer GA — Georgia youth soccer: Concorde Fire, NASA Tophat, UFA, SSA and the Atlanta-area ECNL and MLS Next scene.",
    leagues: [
      { name: "Georgia Soccer & State Cup", description: "Governing body and pathway to US Youth Soccer Southern Regionals." },
      { name: "ECNL Southeast — Georgia clubs", description: "Concorde Fire, NASA Tophat, UFA and SSA anchor ECNL in Georgia." },
      { name: "MLS Next — Atlanta United Academy", description: "One of the top boys' MLS Next academies in the country." },
      { name: "Girls Academy League (GA) — Georgia", description: "Strong Atlanta-metro footprint at top age groups." },
      { name: "Georgia Premier League (GPL)", description: "In-state top division for travel clubs outside national pathways." },
    ],
    tournaments: [
      { name: "Atlanta Cup & Concorde Fire Cup", description: "Two of the largest annual events in the Southeast." },
      { name: "NASA Tophat Invitational", description: "Long-running Atlanta showcase with strong college coach attendance." },
      { name: "Southern Soccer Showcase", description: "Major regional event in the spring." },
    ],
    tryoutWindows: "GA travel and ECNL clubs run tryouts in late May–June. Atlanta United Academy holds year-round ID sessions for MLS Next.",
    collegePipeline: "Georgia routinely produces commits to Georgia, Georgia State, Mercer and the broader SEC/ACC. Atlanta United Academy has signed multiple homegrown players to the MLS first team.",
    faqs: [
      { question: "When are Georgia soccer tryouts?", answer: "Most Georgia travel and ECNL clubs hold tryouts in late May and June. Atlanta United Academy runs MLS Next ID sessions year-round." },
      { question: "Best youth soccer clubs in Georgia?", answer: "Atlanta United Academy, Concorde Fire, NASA Tophat, UFA (United Futbol Academy) and SSA are the most consistently top-recruited clubs in Georgia." },
      { question: "How do I join Atlanta United Academy?", answer: "Atlanta United Academy scouts year-round through MLS Next play and regional ID camps. Open tryouts are rare — most Academy players come through scouted competition." },
      { question: "What is the Georgia Premier League?", answer: "GPL is the top in-state competition for travel clubs not in ECNL or MLS Next. It's where most strong Georgia teams play their weekly league games." },
      { question: "Where do Georgia players go for college?", answer: "Georgia players regularly commit to University of Georgia, Georgia State, Mercer and a broad range of SEC and ACC programs." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "ohio-youth-soccer": {
    state: "Ohio",
    abbreviation: "OH",
    heroTagline:
      "Talking Soccer OH — Ohio youth soccer: Cleveland FC, Internationals SC, Ohio Premier, OPSC, Crew Academy and the Cleveland-Columbus-Cincinnati club scene.",
    leagues: [
      { name: "Ohio North & Ohio South Youth Soccer", description: "Two state associations cover the north and south halves of Ohio." },
      { name: "ECNL Ohio Valley", description: "Internationals SC, Cleveland FC, Ohio Premier and Cincinnati United anchor ECNL in Ohio." },
      { name: "MLS Next — Columbus Crew Academy & FC Cincinnati Academy", description: "Two top boys' MLS Next pathways in the Midwest." },
      { name: "Girls Academy League (GA) — Ohio", description: "Strong footprint in the Cleveland and Columbus metros." },
      { name: "MRL (Midwest Regional League)", description: "Top regional travel league connecting OH clubs with neighboring states." },
    ],
    tournaments: [
      { name: "Crew Cup", description: "Hosted by the Columbus Crew organization — strong MLS Next and ECNL field." },
      { name: "Internationals Memorial Day Showcase", description: "One of the largest tournaments in the Midwest." },
      { name: "Ohio Premier Cup", description: "Long-running Columbus-area showcase." },
    ],
    tryoutWindows: "OH travel and ECNL clubs run tryouts in late May–June. Columbus Crew and FC Cincinnati Academy run year-round ID for MLS Next.",
    collegePipeline: "Ohio consistently sends players to Ohio State, Akron (a longtime men's power), Notre Dame and Big Ten/MAC programs.",
    faqs: [
      { question: "When are Ohio soccer tryouts?", answer: "Most Ohio travel and ECNL clubs hold tryouts in late May and June. Columbus Crew Academy and FC Cincinnati Academy run year-round MLS Next ID sessions." },
      { question: "Best youth soccer clubs in Ohio?", answer: "Columbus Crew Academy, FC Cincinnati Academy, Internationals SC, Cleveland FC, Ohio Premier and Cincinnati United are the most consistently top-recruited clubs in Ohio." },
      { question: "What's the difference between Ohio North and Ohio South Youth Soccer?", answer: "Ohio is split into two state associations: Ohio North Youth Soccer (Cleveland, Toledo, Akron and the northern half) and Ohio South Youth Soccer (Columbus, Cincinnati, Dayton and the south). Each runs its own State Cup." },
      { question: "How do I join Columbus Crew Academy?", answer: "Crew Academy scouts year-round through MLS Next play and regional ID camps. Open tryouts are rare — most Academy players come through scouted competition." },
      { question: "Where do Ohio players go for college?", answer: "Ohio players regularly commit to Ohio State, Akron (a longtime men's soccer power), Notre Dame, Xavier and a range of Big Ten and MAC programs." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "alabama-youth-soccer": {
    state: "Alabama",
    abbreviation: "AL",
    heroTagline:
      "Talking Soccer AL — Alabama youth soccer: AYSA, Birmingham United, Vestavia Hills, Huntsville FC and the Birmingham–Huntsville–Mobile club scene.",
    leagues: [
      { name: "Alabama Soccer Association (AYSA) & State Cup", description: "Governing body for travel and recreational soccer; State Cup feeds US Youth Soccer Southern Regionals." },
      { name: "ECNL Regional League — Southeast", description: "Birmingham United, Vestavia Hills SC and Huntsville FC anchor the ECNL pathway." },
      { name: "MLS Next — Alabama clubs", description: "Birmingham United and Vestavia field MLS Next age groups in the Southeast." },
      { name: "Alabama Premier League (APL)", description: "Top in-state competition for U13–U19 travel teams." },
    ],
    tournaments: [
      { name: "Magic City Classic (Birmingham)", description: "Long-running spring showcase drawing Southeast travel clubs." },
      { name: "Gulf Coast Cup (Mobile)", description: "Coastal showcase popular with AL, MS and FL Panhandle clubs." },
    ],
    tryoutWindows: "AL travel and ECNL clubs run tryouts in late May–June for the following season. Recreational programs register in August.",
    collegePipeline: "Alabama clubs feed Auburn, Alabama, UAB, Samford and Southern Conference programs, with a steady flow to SEC women's programs.",
    faqs: [
      { question: "When are Alabama soccer tryouts?", answer: "Most Alabama travel and ECNL clubs hold tryouts in late May and June for the following season." },
      { question: "Best youth soccer clubs in Alabama?", answer: "Birmingham United, Vestavia Hills SC, Huntsville FC and Mobile United are the most consistently top-recruited clubs in Alabama." },
      { question: "Is there MLS Next in Alabama?", answer: "Yes — Birmingham United and Vestavia field MLS Next age groups, with most boys competing in ECNL Regional League and Alabama Premier League." },
      { question: "How does AYSA State Cup work?", answer: "Alabama Soccer Association runs State Cup each spring. Winners advance to US Youth Soccer Southern Regionals." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "alaska-youth-soccer": {
    state: "Alaska",
    abbreviation: "AK",
    heroTagline:
      "Talking Soccer AK — Alaska youth soccer: Anchorage, Fairbanks and Mat-Su club, indoor and travel talk.",
    leagues: [
      { name: "Alaska Youth Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Far West Regionals." },
      { name: "Cook Inlet Soccer Club & Anchorage Soccer Club", description: "Two of the largest travel programs in the state, based in the Anchorage metro." },
      { name: "Mat-Su Soccer Club", description: "Primary travel program for the Mat-Su Valley north of Anchorage." },
    ],
    tournaments: [
      { name: "Alaska State Cup", description: "Annual statewide championship across age groups." },
      { name: "Lower-48 Travel Showcases", description: "Top AK teams routinely travel to WA and OR for Far West Regional qualifiers and showcases." },
    ],
    tryoutWindows: "Short outdoor season pushes Alaska tryouts into March–April for the summer season. Many clubs run indoor competitive in fall/winter.",
    collegePipeline: "Alaska players typically commit to UAA, UAF and Pacific Northwest D2/NAIA programs, with occasional D1 commits via Far West Regional exposure.",
    faqs: [
      { question: "When are Alaska soccer tryouts?", answer: "Most Alaska travel clubs hold tryouts in March and April given the short outdoor season. Indoor competitive runs fall and winter." },
      { question: "How does Alaska compete with Lower-48 clubs?", answer: "Top AK teams travel to Washington and Oregon for Far West Regional qualifiers and major showcases each year." },
      { question: "Largest soccer clubs in Alaska?", answer: "Cook Inlet Soccer Club, Anchorage Soccer Club and Mat-Su Soccer Club are the largest competitive programs in the state." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "arizona-youth-soccer": {
    state: "Arizona",
    abbreviation: "AZ",
    heroTagline:
      "Talking Soccer AZ — Arizona youth soccer: Phoenix Rising Academy, Real Salt Lake AZ, SC del Sol, Tucson Soccer Academy and the Valley club scene.",
    leagues: [
      { name: "Arizona State Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Far West Regionals." },
      { name: "ECNL Southwest — Arizona", description: "SC del Sol, Phoenix Rising Academy and RSL-AZ anchor the ECNL girls' pathway." },
      { name: "MLS Next — Real Salt Lake AZ & Phoenix Rising Academy", description: "Two of the strongest MLS Next pathways in the Southwest." },
      { name: "Arizona Premier League", description: "Top in-state travel league for U13–U19." },
    ],
    tournaments: [
      { name: "Phoenix College Showcase (Sereno)", description: "Long-running Phoenix recruiting event with heavy national draw." },
      { name: "Far West Regional League", description: "Top AZ teams play into Far West Regional League against CA, NV and HI competition." },
      { name: "RSL-AZ Desert Classic", description: "Major MLS Next and ECNL spring event in the Phoenix metro." },
    ],
    tryoutWindows: "Most AZ travel and ECNL clubs hold tryouts in late May–June. MLS Next ID runs year-round with the warm climate.",
    collegePipeline: "AZ players regularly commit to ASU, U of A, GCU and a wide range of Pac-12 and WCC programs, plus strong recruiting from California D1s.",
    faqs: [
      { question: "When are Arizona soccer tryouts?", answer: "Most AZ travel and ECNL clubs run tryouts in late May and June. RSL-AZ and Phoenix Rising Academy run year-round MLS Next ID sessions." },
      { question: "Best youth soccer clubs in Arizona?", answer: "RSL-AZ, Phoenix Rising Academy, SC del Sol and Tucson Soccer Academy are the most consistently top-recruited clubs in Arizona." },
      { question: "How do I join RSL-AZ Academy?", answer: "RSL-AZ recruits through MLS Next play and regional ID camps. Open tryouts are limited — most players come through scouted competition." },
      { question: "What's the path to Far West Regionals from AZ?", answer: "AZ teams qualify through State Cup or Far West Regional League. Winners advance to US Youth Soccer National Championships." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "arkansas-youth-soccer": {
    state: "Arkansas",
    abbreviation: "AR",
    heroTagline:
      "Talking Soccer AR — Arkansas youth soccer: Arkansas United, Sporting Arkansas, Little Rock and Northwest Arkansas club talk.",
    leagues: [
      { name: "Arkansas State Soccer Association & State Cup", description: "Governing body; State Cup feeds US Youth Soccer Southern Regionals." },
      { name: "ECNL Regional League — South Central", description: "Sporting Arkansas and Arkansas United anchor the ECNL pathway in-state." },
      { name: "Arkansas Premier League", description: "Top in-state travel league for U13–U19 across NWA and central AR." },
    ],
    tournaments: [
      { name: "Hogtown Classic (Fayetteville)", description: "Northwest Arkansas showcase drawing regional ECNL and elite travel clubs." },
      { name: "Sporting Arkansas Showcase", description: "Spring event hosted by Sporting Arkansas with college recruiter attendance." },
    ],
    tryoutWindows: "AR travel and ECNL clubs run tryouts in late May–June for the following season.",
    collegePipeline: "Arkansas players regularly commit to Arkansas, Arkansas State, Tulsa and Southern Conference programs.",
    faqs: [
      { question: "When are Arkansas soccer tryouts?", answer: "Most Arkansas travel and ECNL clubs hold tryouts in late May and June for the following season." },
      { question: "Best youth soccer clubs in Arkansas?", answer: "Sporting Arkansas and Arkansas United are the most consistently top-recruited clubs in Arkansas, with a strong Northwest Arkansas club scene." },
      { question: "How does Arkansas State Cup work?", answer: "Arkansas State Soccer Association runs State Cup each spring. Winners advance to US Youth Soccer Southern Regionals." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "colorado-youth-soccer": {
    state: "Colorado",
    abbreviation: "CO",
    heroTagline:
      "Talking Soccer CO — Colorado youth soccer: Real Colorado, Rapids Youth Soccer, Colorado Rush, Pride SC and the Front Range club scene.",
    leagues: [
      { name: "Colorado Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Far West Regionals." },
      { name: "ECNL Mountain — Colorado", description: "Real Colorado, Colorado Rush, Pride SC and Storm anchor the ECNL pathway." },
      { name: "MLS Next — Rapids Youth Soccer & Real Colorado", description: "Two of the top MLS Next pathways in the Mountain region." },
      { name: "Colorado Premier League", description: "Top in-state travel league for U13–U19." },
    ],
    tournaments: [
      { name: "Colorado Rush Tournament", description: "Major Front Range showcase drawing regional ECNL and MLS Next clubs." },
      { name: "Real Colorado Cup", description: "Premier spring event hosted by Real Colorado." },
      { name: "Vail Invitational", description: "Mountain-town showcase popular with Rocky Mountain region travel clubs." },
    ],
    tryoutWindows: "Most CO travel and ECNL clubs hold tryouts in late May–June. MLS Next ID runs year-round.",
    collegePipeline: "Colorado clubs feed Denver, Air Force, Colorado College, CSU and a wide range of Pac-12, Big 12 and WCC programs.",
    faqs: [
      { question: "When are Colorado soccer tryouts?", answer: "Most CO travel and ECNL clubs run tryouts in late May and June. Real Colorado and Rapids Youth Soccer run year-round MLS Next ID sessions." },
      { question: "Best youth soccer clubs in Colorado?", answer: "Real Colorado, Rapids Youth Soccer, Colorado Rush, Pride SC and Storm are the most consistently top-recruited clubs in Colorado." },
      { question: "How do I join Rapids Academy?", answer: "Rapids Youth Soccer recruits through MLS Next play and regional ID camps. Open tryouts are limited — most Academy players come through scouted competition." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "delaware-youth-soccer": {
    state: "Delaware",
    abbreviation: "DE",
    heroTagline:
      "Talking Soccer DE — Delaware youth soccer: Delaware Rush, Diamond State, Newark Soccer Club and the Wilmington–Newark club scene.",
    leagues: [
      { name: "Delaware Youth Soccer Association & State Cup", description: "Governing body; State Cup pathway to US Youth Soccer Eastern Regionals." },
      { name: "EDP & MAPS", description: "Most DE clubs play EDP/MAPS for stronger regional weekly competition." },
      { name: "ECNL Regional League — Mid-Atlantic", description: "Delaware Rush and Diamond State field ECNL Regional age groups." },
    ],
    tournaments: [
      { name: "Delaware Rush Memorial Day Tournament", description: "Long-running Wilmington-area showcase." },
      { name: "Jefferson Cup (Richmond)", description: "Most top DE teams target Jeff Cup each March." },
    ],
    tryoutWindows: "DE travel clubs run tryouts in late May–June for the following season.",
    collegePipeline: "Delaware players commit to Delaware, Drexel, Temple and a steady flow of Patriot League and CAA programs.",
    faqs: [
      { question: "When are Delaware soccer tryouts?", answer: "Most Delaware travel clubs hold tryouts in late May and June for the following season." },
      { question: "Best youth soccer clubs in Delaware?", answer: "Delaware Rush, Diamond State and Newark Soccer Club are the most consistently top-recruited clubs in Delaware." },
      { question: "Do DE clubs play EDP?", answer: "Yes — most Delaware travel clubs play EDP and MAPS for stronger weekly competition against PA, NJ and MD clubs." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "hawaii-youth-soccer": {
    state: "Hawaii",
    abbreviation: "HI",
    heroTagline:
      "Talking Soccer HI — Hawaii youth soccer: HSC, Albion Hawaii, Rush Hawaii and Oahu, Maui and Big Island club talk.",
    leagues: [
      { name: "Hawaii Youth Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Far West Regionals." },
      { name: "ECNL Regional League — Southwest", description: "Albion Hawaii and Rush Hawaii field ECNL Regional age groups." },
      { name: "Hawaii Premier League", description: "Top in-state travel league for U13–U19 across the islands." },
    ],
    tournaments: [
      { name: "Pacific Rim Cup", description: "International-flavored Honolulu showcase drawing regional and Pacific clubs." },
      { name: "Far West Regional League", description: "Top HI teams travel to the mainland each spring for Far West Regional play." },
    ],
    tryoutWindows: "Year-round climate lets HI clubs run rolling tryouts; most align with mainland late May–June timing.",
    collegePipeline: "Hawaii players regularly commit to Hawaii, HPU, Chaminade and West Coast D1/D2 programs.",
    faqs: [
      { question: "When are Hawaii soccer tryouts?", answer: "Most Hawaii travel clubs hold tryouts in late May and June, aligning with mainland calendars, though the year-round climate enables rolling tryouts." },
      { question: "Best youth soccer clubs in Hawaii?", answer: "Albion Hawaii, Rush Hawaii and HSC are the most consistently top-recruited clubs in Hawaii." },
      { question: "How do HI teams qualify for Regionals?", answer: "Hawaii teams qualify through State Cup or Far West Regional League and travel to the mainland for postseason play." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "idaho-youth-soccer": {
    state: "Idaho",
    abbreviation: "ID",
    heroTagline:
      "Talking Soccer ID — Idaho youth soccer: Idaho Rush, Boise Nationals, IDA, Capital FC and Boise–Treasure Valley club talk.",
    leagues: [
      { name: "Idaho Youth Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Far West Regionals." },
      { name: "RCL crossover (Northwest)", description: "Top ID clubs often play into Washington's RCL for tougher weekly competition." },
      { name: "ECNL Regional League — Northwest", description: "Idaho Rush, Boise Nationals and Capital FC field ECNL Regional age groups." },
    ],
    tournaments: [
      { name: "Sun Valley Soccer Tournament", description: "Iconic Idaho summer event drawing Pacific Northwest clubs." },
      { name: "Boise Cup", description: "Treasure Valley showcase hosted by area clubs." },
    ],
    tryoutWindows: "ID travel and ECNL clubs run tryouts in late May–June for the following season.",
    collegePipeline: "Idaho players regularly commit to Boise State, Idaho, BYU and a steady flow of WCC and Pac-12 programs.",
    faqs: [
      { question: "When are Idaho soccer tryouts?", answer: "Most Idaho travel and ECNL clubs hold tryouts in late May and June for the following season." },
      { question: "Best youth soccer clubs in Idaho?", answer: "Idaho Rush, Boise Nationals, Capital FC and IDA are the most consistently top-recruited clubs in Idaho." },
      { question: "Do ID teams play RCL?", answer: "Yes — top Idaho clubs often play into Washington's RCL (Regional Club League) for stronger weekly competition than in-state-only leagues." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "illinois-youth-soccer": {
    state: "Illinois",
    abbreviation: "IL",
    heroTagline:
      "Talking Soccer IL — Illinois youth soccer: Chicago FC United, Sockers FC, Eclipse Select, FC United, Chicago Fire Juniors and the Chicagoland club scene.",
    leagues: [
      { name: "Illinois Youth Soccer & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Midwest Regionals." },
      { name: "ECNL Midwest — Illinois", description: "Eclipse Select, Chicago FC United, Sockers FC and FC United anchor the ECNL pathway." },
      { name: "MLS Next — Chicago Fire Juniors & Sockers FC", description: "Top boys' MLS Next pathways in the Midwest." },
      { name: "MRL (Midwest Regional League)", description: "Top regional travel league connecting IL with WI, IN, MI and OH." },
    ],
    tournaments: [
      { name: "Eclipse Select Showcase", description: "Major Chicagoland recruiting event with strong national draw." },
      { name: "Schaumburg Soccerfest", description: "Long-running Chicagoland tournament hosted by FC United." },
      { name: "Chicago Fire Cup", description: "Hosted by Chicago Fire Juniors with strong MLS Next field." },
    ],
    tryoutWindows: "IL travel and ECNL clubs run tryouts in late May–June. Chicago Fire Juniors run year-round MLS Next ID.",
    collegePipeline: "Illinois clubs feed Northwestern, Loyola, Notre Dame, Indiana and a steady flow of Big Ten and Big East programs.",
    faqs: [
      { question: "When are Illinois soccer tryouts?", answer: "Most Illinois travel and ECNL clubs hold tryouts in late May and June. Chicago Fire Juniors run year-round MLS Next ID sessions." },
      { question: "Best youth soccer clubs in Illinois?", answer: "Eclipse Select, Chicago FC United, Sockers FC, FC United and Chicago Fire Juniors are the most consistently top-recruited clubs in Illinois." },
      { question: "How do I join Chicago Fire Academy?", answer: "Chicago Fire Juniors recruit through MLS Next play and regional ID camps. Open tryouts are limited — most Academy players come through scouted competition." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "indiana-youth-soccer": {
    state: "Indiana",
    abbreviation: "IN",
    heroTagline:
      "Talking Soccer IN — Indiana youth soccer: Indy Eleven Youth, Carmel United, FC Pride, Indiana Fire and the Indianapolis–Fort Wayne club scene.",
    leagues: [
      { name: "Indiana Soccer & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Midwest Regionals." },
      { name: "ECNL Ohio Valley — Indiana", description: "Indiana Fire and Carmel United anchor the ECNL pathway in-state." },
      { name: "MLS Next — Indy Eleven Youth Academy", description: "Indiana's flagship boys' MLS Next pathway." },
      { name: "MRL (Midwest Regional League)", description: "Top regional travel league connecting IN with neighboring states." },
    ],
    tournaments: [
      { name: "Indiana Fire Juniors Showcase", description: "Major Indianapolis recruiting event." },
      { name: "Carmel Cup", description: "Long-running Carmel United showcase drawing Midwest clubs." },
    ],
    tryoutWindows: "IN travel and ECNL clubs run tryouts in late May–June for the following season.",
    collegePipeline: "Indiana clubs feed Indiana (a longtime men's power), Notre Dame, Butler, Purdue and a wide range of Big Ten programs.",
    faqs: [
      { question: "When are Indiana soccer tryouts?", answer: "Most Indiana travel and ECNL clubs hold tryouts in late May and June for the following season." },
      { question: "Best youth soccer clubs in Indiana?", answer: "Indiana Fire, Carmel United, FC Pride and Indy Eleven Youth Academy are the most consistently top-recruited clubs in Indiana." },
      { question: "How strong is Indiana men's college soccer?", answer: "Indiana University is a longtime national men's soccer power and a primary destination for top IN players, alongside Notre Dame and Butler." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "iowa-youth-soccer": {
    state: "Iowa",
    abbreviation: "IA",
    heroTagline:
      "Talking Soccer IA — Iowa youth soccer: Iowa Rush, Sporting Iowa, Cedar Rapids, Des Moines Menace Academy and Iowa club talk.",
    leagues: [
      { name: "Iowa Soccer & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Midwest Regionals." },
      { name: "ECNL Regional League — Heartland", description: "Iowa Rush and Sporting Iowa field ECNL Regional age groups." },
      { name: "MRL (Midwest Regional League)", description: "Top IA clubs play MRL for stronger weekly competition." },
    ],
    tournaments: [
      { name: "Iowa Rush Memorial Day", description: "Long-running Des Moines area showcase." },
      { name: "Schwan's USA Cup crossover (MN)", description: "Many top IA clubs travel to Minnesota's Schwan's USA Cup each summer." },
    ],
    tryoutWindows: "IA travel clubs run tryouts in late May–June for the following season.",
    collegePipeline: "Iowa players regularly commit to Iowa, Iowa State, Drake, Creighton and Big Ten/Big 12 programs.",
    faqs: [
      { question: "When are Iowa soccer tryouts?", answer: "Most Iowa travel clubs hold tryouts in late May and June for the following season." },
      { question: "Best youth soccer clubs in Iowa?", answer: "Iowa Rush, Sporting Iowa and Des Moines Menace Academy are the most consistently top-recruited clubs in Iowa." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "kansas-youth-soccer": {
    state: "Kansas",
    abbreviation: "KS",
    heroTagline:
      "Talking Soccer KS — Kansas youth soccer: Sporting KC Academy, KC Athletics, Heartland Soccer League, OPSC and the Kansas City–Wichita club scene.",
    leagues: [
      { name: "Kansas Youth Soccer & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Midwest Regionals." },
      { name: "Heartland Soccer League", description: "One of the largest youth soccer leagues in the country — anchors weekly play in KS and western MO." },
      { name: "ECNL Heartland — Kansas", description: "Sporting Blue Valley, KC Athletics and Sporting KC Academy anchor the ECNL pathway." },
      { name: "MLS Next — Sporting KC Academy", description: "One of the top boys' MLS Next pathways in the Midwest." },
    ],
    tournaments: [
      { name: "Sporting KC Champions Cup", description: "Major Kansas City showcase with strong MLS Next field." },
      { name: "KC Invitational", description: "Long-running Kansas City–area showcase drawing Midwest clubs." },
    ],
    tryoutWindows: "KS travel and ECNL clubs run tryouts in late May–June. Sporting KC Academy runs year-round MLS Next ID.",
    collegePipeline: "Kansas players regularly commit to Kansas, Kansas State, Creighton and a wide range of Big 12 and Missouri Valley programs.",
    faqs: [
      { question: "When are Kansas soccer tryouts?", answer: "Most Kansas travel and ECNL clubs hold tryouts in late May and June. Sporting KC Academy runs year-round MLS Next ID sessions." },
      { question: "Best youth soccer clubs in Kansas?", answer: "Sporting KC Academy, Sporting Blue Valley, KC Athletics and Wichita Wings are the most consistently top-recruited clubs in Kansas." },
      { question: "What is Heartland Soccer League?", answer: "Heartland Soccer League is one of the largest youth soccer leagues in the country, anchoring weekly play across Kansas and western Missouri." },
      { question: "How do I join Sporting KC Academy?", answer: "Sporting KC Academy recruits through MLS Next play and regional ID camps. Open tryouts are limited — most Academy players come through scouted competition." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "kentucky-youth-soccer": {
    state: "Kentucky",
    abbreviation: "KY",
    heroTagline:
      "Talking Soccer KY — Kentucky youth soccer: Javanon FC, Kings Hammer, Louisville City Academy and the Louisville–Lexington–Northern KY club scene.",
    leagues: [
      { name: "Kentucky Youth Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Southern Regionals." },
      { name: "ECNL Ohio Valley — Kentucky", description: "Javanon FC and Kings Hammer anchor the ECNL pathway in-state." },
      { name: "MLS Next — Louisville City Academy", description: "Kentucky's flagship boys' MLS Next pathway." },
      { name: "Kentucky Premier League", description: "Top in-state travel league for U13–U19." },
    ],
    tournaments: [
      { name: "Javanon Spring Classic", description: "Major Louisville-area recruiting event." },
      { name: "Kings Hammer Showcase", description: "Northern Kentucky / Cincinnati showcase with strong MRL and ECNL field." },
    ],
    tryoutWindows: "KY travel and ECNL clubs run tryouts in late May–June. Louisville City Academy runs year-round MLS Next ID.",
    collegePipeline: "Kentucky players regularly commit to Louisville, Kentucky, Western Kentucky and SEC/ACC programs.",
    faqs: [
      { question: "When are Kentucky soccer tryouts?", answer: "Most Kentucky travel and ECNL clubs hold tryouts in late May and June." },
      { question: "Best youth soccer clubs in Kentucky?", answer: "Javanon FC, Kings Hammer and Louisville City Academy are the most consistently top-recruited clubs in Kentucky." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "louisiana-youth-soccer": {
    state: "Louisiana",
    abbreviation: "LA",
    heroTagline:
      "Talking Soccer LA — Louisiana youth soccer: LSA, Baton Rouge SC, Louisiana Fire, Lafayette Soccer Club and New Orleans area club talk.",
    leagues: [
      { name: "Louisiana Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Southern Regionals." },
      { name: "ECNL Regional League — South Central", description: "Baton Rouge SC and Louisiana Fire field ECNL Regional age groups." },
      { name: "Louisiana Premier League", description: "Top in-state travel league for U13–U19." },
    ],
    tournaments: [
      { name: "Crescent City Classic (New Orleans)", description: "Long-running Gulf Coast showcase." },
      { name: "Cajun Cup (Lafayette)", description: "Acadiana showcase popular with LA and TX clubs." },
    ],
    tryoutWindows: "LA travel and ECNL clubs run tryouts in late May–June for the following season.",
    collegePipeline: "Louisiana players regularly commit to LSU, Tulane, Louisiana Tech and SEC/Sun Belt programs.",
    faqs: [
      { question: "When are Louisiana soccer tryouts?", answer: "Most Louisiana travel and ECNL clubs hold tryouts in late May and June." },
      { question: "Best youth soccer clubs in Louisiana?", answer: "Baton Rouge SC, Louisiana Fire and Lafayette Soccer Club are the most consistently top-recruited clubs in Louisiana." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "maine-youth-soccer": {
    state: "Maine",
    abbreviation: "ME",
    heroTagline:
      "Talking Soccer ME — Maine youth soccer: Seacoast United Maine, Portland-area travel, MYSA and the Greater Portland and Bangor club scene.",
    leagues: [
      { name: "Maine Youth Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Eastern Regionals." },
      { name: "NEP (New England Premiership)", description: "Top regional travel league connecting ME with MA, NH, RI and CT clubs." },
      { name: "ECNL Regional League — New England", description: "Seacoast United Maine and other top clubs field ECNL Regional age groups." },
    ],
    tournaments: [
      { name: "Seacoast Memorial Day Tournament", description: "Major New England showcase drawing top ME, NH and MA teams." },
      { name: "Maine State Cup", description: "Annual statewide championship with Eastern Regional pathway." },
    ],
    tryoutWindows: "ME travel clubs run tryouts in late May–June for the following season; recreational programs register in August.",
    collegePipeline: "Maine players regularly commit to Maine, USM, Bowdoin, Bates, Colby and a steady flow of Patriot League and NESCAC programs.",
    faqs: [
      { question: "When are Maine soccer tryouts?", answer: "Most Maine travel clubs hold tryouts in late May and June for the following season." },
      { question: "Best youth soccer clubs in Maine?", answer: "Seacoast United Maine is the most consistently top-recruited program in Maine, with a strong Greater Portland travel scene." },
      { question: "Do ME clubs play NEP?", answer: "Yes — top Maine clubs often play into NEP (New England Premiership) for stronger weekly competition than in-state-only leagues." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "maryland-youth-soccer": {
    state: "Maryland",
    abbreviation: "MD",
    heroTagline:
      "Talking Soccer MD — Maryland youth soccer: Bethesda SC, Pipeline SC, Baltimore Armour, Arlington Soccer and the DMV club scene.",
    leagues: [
      { name: "Maryland State Youth Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Eastern Regionals." },
      { name: "ECNL Mid-Atlantic", description: "Bethesda SC, Pipeline SC, McLean YS and Loudoun Soccer anchor the ECNL pathway across the DMV." },
      { name: "MLS Next — Baltimore Armour & DC United Academy", description: "Two top boys' MLS Next pathways in the Mid-Atlantic." },
      { name: "EDP & NCSL", description: "Most DMV clubs play EDP and the National Capital Soccer League for weekly competition." },
    ],
    tournaments: [
      { name: "Bethesda Premier Cup", description: "Major Mid-Atlantic showcase with strong national draw." },
      { name: "Jefferson Cup (Richmond)", description: "Most top MD travel teams target Jeff Cup each March." },
      { name: "Pipeline Showcase", description: "Long-running Baltimore-area recruiting event." },
    ],
    tryoutWindows: "MD travel and ECNL clubs run tryouts in late May–June. MLS Next ID runs year-round.",
    collegePipeline: "Maryland produces a steady stream of D1 commits to Maryland, Georgetown, Navy, Loyola and a wide range of ACC and Patriot League programs.",
    faqs: [
      { question: "When are Maryland soccer tryouts?", answer: "Most Maryland travel and ECNL clubs run tryouts in late May and June. Baltimore Armour and DC United Academy run year-round MLS Next ID sessions." },
      { question: "Best ECNL clubs in Maryland?", answer: "Bethesda SC, Pipeline SC and Baltimore Celtic are among the most consistently top-recruited ECNL programs in Maryland." },
      { question: "What's the DMV soccer scene?", answer: "The DMV (DC, Maryland, Virginia) is one of the deepest youth soccer regions in the country, with EDP, NCSL, ECNL Mid-Atlantic and MLS Next all running weekly competition across state lines." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "michigan-youth-soccer": {
    state: "Michigan",
    abbreviation: "MI",
    heroTagline:
      "Talking Soccer MI — Michigan youth soccer: Michigan Hawks, Vardar, Nationals, MJSC and the Detroit metro and West Michigan club scene.",
    leagues: [
      { name: "Michigan Youth Soccer Association (MYSA) & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Midwest Regionals." },
      { name: "ECNL Great Lakes — Michigan", description: "Michigan Hawks, Vardar and Nationals anchor the ECNL pathway." },
      { name: "MLS Next — Michigan", description: "Vardar, Nationals and MUSC field strong MLS Next age groups." },
      { name: "MRL (Midwest Regional League)", description: "Top regional travel league connecting MI with OH, IN and IL." },
    ],
    tournaments: [
      { name: "Vardar Showcase", description: "Major Detroit-metro recruiting event with strong national draw." },
      { name: "Michigan Hawks Memorial Day", description: "Long-running Detroit-area showcase popular with ECNL clubs." },
    ],
    tryoutWindows: "MI travel and ECNL clubs run tryouts in late May–June for the following season.",
    collegePipeline: "Michigan clubs feed Michigan, Michigan State, Notre Dame, Western Michigan and a wide range of Big Ten programs.",
    faqs: [
      { question: "When are Michigan soccer tryouts?", answer: "Most Michigan travel and ECNL clubs hold tryouts in late May and June for the following season." },
      { question: "Best youth soccer clubs in Michigan?", answer: "Michigan Hawks, Vardar, Nationals and MUSC are the most consistently top-recruited clubs in Michigan." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "minnesota-youth-soccer": {
    state: "Minnesota",
    abbreviation: "MN",
    heroTagline:
      "Talking Soccer MN — Minnesota youth soccer: Minnesota Thunder Academy, Shattuck-St. Mary's, MN United Academy, Joy of the People and the Twin Cities club scene.",
    leagues: [
      { name: "Minnesota Youth Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Midwest Regionals." },
      { name: "ECNL Heartland — Minnesota", description: "Minnesota Thunder Academy and Joy of the People anchor the ECNL pathway." },
      { name: "MLS Next — Minnesota United Academy", description: "Minnesota's flagship boys' MLS Next pathway." },
      { name: "MN Premier League", description: "Top in-state travel league for U13–U19." },
    ],
    tournaments: [
      { name: "Schwan's USA Cup (Blaine)", description: "One of the largest youth soccer tournaments in the world — held annually at the National Sports Center." },
      { name: "MN Thunder Showcase", description: "Major Twin Cities recruiting event." },
    ],
    tryoutWindows: "MN travel and ECNL clubs run tryouts in late May–June. MN United Academy runs year-round MLS Next ID.",
    collegePipeline: "Minnesota players regularly commit to Minnesota, Marquette, Creighton, St. Thomas and a wide range of Big Ten and Big East programs.",
    faqs: [
      { question: "When are Minnesota soccer tryouts?", answer: "Most Minnesota travel and ECNL clubs hold tryouts in late May and June for the following season." },
      { question: "Best youth soccer clubs in Minnesota?", answer: "Minnesota Thunder Academy, Joy of the People, MN United Academy and Shattuck-St. Mary's are the most consistently top-recruited programs in Minnesota." },
      { question: "What is Schwan's USA Cup?", answer: "Schwan's USA Cup is one of the largest youth soccer tournaments in the world, held annually at the National Sports Center in Blaine, MN, drawing teams from across North America and beyond." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "mississippi-youth-soccer": {
    state: "Mississippi",
    abbreviation: "MS",
    heroTagline:
      "Talking Soccer MS — Mississippi youth soccer: MSA, Jackson Futbol Club, Mississippi Brilla and the Jackson–Gulf Coast club scene.",
    leagues: [
      { name: "Mississippi Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Southern Regionals." },
      { name: "ECNL Regional League — Southeast", description: "Jackson Futbol Club and Mississippi Brilla field ECNL Regional age groups." },
      { name: "Mississippi Premier League", description: "Top in-state travel league for U13–U19." },
    ],
    tournaments: [
      { name: "Magnolia Cup (Jackson)", description: "Long-running spring showcase drawing Southeast travel clubs." },
      { name: "Gulf Coast Cup crossover", description: "Top MS clubs travel to the Gulf Coast region showcases each year." },
    ],
    tryoutWindows: "MS travel and ECNL clubs run tryouts in late May–June for the following season.",
    collegePipeline: "Mississippi players regularly commit to Ole Miss, Mississippi State, Southern Miss and a steady flow of SEC and Sun Belt programs.",
    faqs: [
      { question: "When are Mississippi soccer tryouts?", answer: "Most Mississippi travel and ECNL clubs hold tryouts in late May and June." },
      { question: "Best youth soccer clubs in Mississippi?", answer: "Jackson Futbol Club and Mississippi Brilla are among the most consistently top-recruited clubs in Mississippi." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "missouri-youth-soccer": {
    state: "Missouri",
    abbreviation: "MO",
    heroTagline:
      "Talking Soccer MO — Missouri youth soccer: St. Louis Scott Gallagher, Sporting STL, Lou Fusz, Sporting KC affiliates and the STL–KC club scene.",
    leagues: [
      { name: "Missouri Youth Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Midwest Regionals." },
      { name: "ECNL Heartland — Missouri", description: "St. Louis Scott Gallagher, Lou Fusz and Sporting STL anchor the ECNL pathway." },
      { name: "MLS Next — St. Louis City SC Academy & Sporting STL", description: "Two flagship boys' MLS Next pathways in the Midwest." },
      { name: "Heartland Soccer League", description: "Anchors weekly play across western MO and KS." },
    ],
    tournaments: [
      { name: "SLSG Spring Showcase", description: "Major St. Louis recruiting event." },
      { name: "Lou Fusz College Showcase", description: "Long-running STL recruiting event drawing national clubs." },
    ],
    tryoutWindows: "MO travel and ECNL clubs run tryouts in late May–June. St. Louis City SC Academy runs year-round MLS Next ID.",
    collegePipeline: "Missouri produces a steady stream of D1 commits — St. Louis is a longtime US soccer hotbed feeding Missouri, SLU, Creighton, Notre Dame and a wide range of programs.",
    faqs: [
      { question: "When are Missouri soccer tryouts?", answer: "Most Missouri travel and ECNL clubs hold tryouts in late May and June. St. Louis City SC Academy runs year-round MLS Next ID sessions." },
      { question: "Best youth soccer clubs in Missouri?", answer: "St. Louis Scott Gallagher, Sporting STL, Lou Fusz, St. Louis City SC Academy and Sporting KC affiliates are the most consistently top-recruited clubs in Missouri." },
      { question: "Why is St. Louis a soccer hotbed?", answer: "St. Louis has one of the deepest US soccer traditions, with SLU's historic men's program, strong club infrastructure (SLSG, Lou Fusz, Sporting STL) and now St. Louis City SC Academy as the MLS Next anchor." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "montana-youth-soccer": {
    state: "Montana",
    abbreviation: "MT",
    heroTagline:
      "Talking Soccer MT — Montana youth soccer: Bitterroot, Missoula Strikers, Treasure State and the Missoula–Bozeman–Billings club talk.",
    leagues: [
      { name: "Montana Youth Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Far West Regionals." },
      { name: "Montana Premier League", description: "Top in-state travel league for U13–U19." },
      { name: "RCL crossover", description: "Top MT clubs occasionally play into Washington's RCL or travel for Far West Regional play." },
    ],
    tournaments: [
      { name: "Treasure State Cup", description: "Statewide showcase popular with Mountain region clubs." },
      { name: "Far West Regional travel", description: "Top MT teams travel to WA, OR or UT each spring for Far West Regional play." },
    ],
    tryoutWindows: "MT travel clubs run tryouts in late May–June; short season pushes some clubs to indoor competitive in winter.",
    collegePipeline: "Montana players regularly commit to Montana, Montana State, Carroll College and a steady flow of Pacific Northwest D2/NAIA programs.",
    faqs: [
      { question: "When are Montana soccer tryouts?", answer: "Most Montana travel clubs hold tryouts in late May and June." },
      { question: "Best youth soccer clubs in Montana?", answer: "Missoula Strikers, Bitterroot and Treasure State are among the most active competitive programs in Montana." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "nebraska-youth-soccer": {
    state: "Nebraska",
    abbreviation: "NE",
    heroTagline:
      "Talking Soccer NE — Nebraska youth soccer: NSC, Omaha FC, Sporting Omaha, Lincoln Stars and the Omaha–Lincoln club scene.",
    leagues: [
      { name: "Nebraska State Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Midwest Regionals." },
      { name: "ECNL Regional League — Heartland", description: "Sporting Omaha FC, NSC and Lincoln Stars field ECNL Regional age groups." },
      { name: "Heartland Soccer League crossover", description: "Top NE clubs play into Heartland Soccer League with KS and MO competition." },
    ],
    tournaments: [
      { name: "Sporting Omaha Memorial Day Cup", description: "Long-running Omaha-area recruiting event." },
      { name: "Cornhusker State Games (Lincoln)", description: "Statewide event with strong youth soccer field." },
    ],
    tryoutWindows: "NE travel and ECNL clubs run tryouts in late May–June for the following season.",
    collegePipeline: "Nebraska players regularly commit to Creighton, Nebraska, Omaha and a wide range of Big Ten/Big East programs.",
    faqs: [
      { question: "When are Nebraska soccer tryouts?", answer: "Most Nebraska travel and ECNL clubs hold tryouts in late May and June." },
      { question: "Best youth soccer clubs in Nebraska?", answer: "Sporting Omaha FC, NSC, Omaha FC and Lincoln Stars are the most consistently top-recruited clubs in Nebraska." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "nevada-youth-soccer": {
    state: "Nevada",
    abbreviation: "NV",
    heroTagline:
      "Talking Soccer NV — Nevada youth soccer: Las Vegas SA, Players SC, Heat FC, Reno United and the Las Vegas–Reno club scene.",
    leagues: [
      { name: "Nevada Youth Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Far West Regionals." },
      { name: "ECNL Southwest — Nevada", description: "Las Vegas Soccer Academy, Players SC and Heat FC field ECNL age groups." },
      { name: "MLS Next — Nevada clubs", description: "Las Vegas SA fields MLS Next age groups, with most NV boys also competing in Far West Regional League." },
      { name: "Far West Regional League", description: "Top NV teams play into Far West Regional League against CA, AZ and HI clubs." },
    ],
    tournaments: [
      { name: "Las Vegas Players Showcase", description: "Major recruiting event drawing national ECNL and MLS Next clubs." },
      { name: "Mayor's Cup International Showcase (Las Vegas)", description: "Long-running Las Vegas showcase with strong international draw." },
    ],
    tryoutWindows: "NV travel and ECNL clubs run tryouts in late May–June. Year-round climate enables rolling ID sessions.",
    collegePipeline: "Nevada players regularly commit to UNLV, Nevada and a steady flow of Pac-12, WCC and Mountain West programs.",
    faqs: [
      { question: "When are Nevada soccer tryouts?", answer: "Most Nevada travel and ECNL clubs hold tryouts in late May and June." },
      { question: "Best youth soccer clubs in Nevada?", answer: "Las Vegas Soccer Academy, Players SC, Heat FC and Reno United are the most consistently top-recruited clubs in Nevada." },
      { question: "How big are Las Vegas showcases?", answer: "Las Vegas hosts several of the country's largest youth soccer showcases each year — the Mayor's Cup and Las Vegas Players Showcase draw national clubs and recruiters." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "new-hampshire-youth-soccer": {
    state: "New Hampshire",
    abbreviation: "NH",
    heroTagline:
      "Talking Soccer NH — New Hampshire youth soccer: Seacoast United, NH Soccer Club, GPS NH and the Seacoast and Manchester club scene.",
    leagues: [
      { name: "New Hampshire Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Eastern Regionals." },
      { name: "NEP (New England Premiership)", description: "Top regional travel league connecting NH with MA, ME, RI and CT clubs." },
      { name: "ECNL New England — NH clubs", description: "Seacoast United is a longtime ECNL anchor with a major NH footprint." },
    ],
    tournaments: [
      { name: "Seacoast Memorial Day Tournament", description: "Major New England showcase drawing top ECNL and elite travel clubs." },
      { name: "Seacoast United Fall Showcase", description: "Long-running fall recruiting event in Epping/Hampton." },
    ],
    tryoutWindows: "NH travel and ECNL clubs run tryouts in late May–June for the following season.",
    collegePipeline: "New Hampshire players regularly commit to UNH, Dartmouth, Plymouth State and a steady flow of Patriot League and NESCAC programs.",
    faqs: [
      { question: "When are New Hampshire soccer tryouts?", answer: "Most NH travel and ECNL clubs hold tryouts in late May and June for the following season." },
      { question: "Best youth soccer clubs in New Hampshire?", answer: "Seacoast United is the longtime anchor program in NH, with GPS NH and NH Soccer Club also fielding strong age groups." },
      { question: "Do NH clubs play NEP?", answer: "Yes — most top NH clubs play NEP for stronger weekly competition across New England." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "new-mexico-youth-soccer": {
    state: "New Mexico",
    abbreviation: "NM",
    heroTagline:
      "Talking Soccer NM — New Mexico youth soccer: Albuquerque United, Rio Rapids, New Mexico Rush and the Albuquerque–Santa Fe–Las Cruces club scene.",
    leagues: [
      { name: "New Mexico Youth Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Far West Regionals." },
      { name: "ECNL Regional League — Southwest", description: "Albuquerque United, Rio Rapids and New Mexico Rush field ECNL Regional age groups." },
      { name: "New Mexico Premier League", description: "Top in-state travel league for U13–U19." },
    ],
    tournaments: [
      { name: "Albuquerque Soccer Tournament", description: "Long-running Southwest showcase." },
      { name: "Rio Rapids Spring Showcase", description: "Regional recruiting event drawing Mountain region clubs." },
    ],
    tryoutWindows: "NM travel and ECNL clubs run tryouts in late May–June for the following season.",
    collegePipeline: "New Mexico players regularly commit to UNM, New Mexico State and a steady flow of Mountain West and Pac-12 programs.",
    faqs: [
      { question: "When are New Mexico soccer tryouts?", answer: "Most NM travel and ECNL clubs hold tryouts in late May and June." },
      { question: "Best youth soccer clubs in New Mexico?", answer: "Albuquerque United, Rio Rapids and New Mexico Rush are the most consistently top-recruited clubs in New Mexico." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "new-york-youth-soccer": {
    state: "New York",
    abbreviation: "NY",
    heroTagline:
      "Talking Soccer NY — New York youth soccer: NY Red Bulls Academy, NYSC, Albertson SC, Manhattan SC, BW Gottschee and the NYC, Long Island and Westchester club scene.",
    leagues: [
      { name: "Eastern New York Youth Soccer Association & State Cup (ENY)", description: "Covers NYC, Long Island and the Hudson Valley." },
      { name: "Western/Central NY State Soccer & State Cups", description: "Cover Western and Central NY including Buffalo, Rochester and Syracuse." },
      { name: "ECNL Northeast — New York", description: "Albertson SC, World Class FC, FC Westchester and Manhattan SC anchor the ECNL pathway." },
      { name: "MLS Next — NY Red Bulls Academy, NYCFC & BW Gottschee", description: "Three of the strongest MLS Next pathways in the country." },
      { name: "EDP, LIJSL & ENYYSA", description: "Anchor weekly travel competition across Long Island and the metro area." },
    ],
    tournaments: [
      { name: "Manhattan Kickoff", description: "Major early-season showcase drawing top East Coast travel clubs." },
      { name: "Jefferson Cup (Richmond)", description: "Most top NY travel teams target Jeff Cup each March." },
      { name: "Red Bull Cup", description: "Hosted by NY Red Bulls Academy with strong MLS Next field." },
      { name: "Cedar Stars Cup (NJ crossover)", description: "Many NY clubs cross to NJ for Cedar Stars showcases." },
    ],
    tryoutWindows: "NY travel and ECNL clubs run tryouts in late May–June. NY Red Bulls Academy, NYCFC and BW Gottschee run year-round MLS Next ID.",
    collegePipeline: "New York is one of the deepest pipelines in the country — feeding Syracuse, Columbia, Cornell, St. John's, Hofstra and a wide range of ACC, Big East and Patriot League programs. NYRB Academy regularly produces MLS homegrowns.",
    faqs: [
      { question: "When are New York soccer tryouts?", answer: "Most NY travel and ECNL clubs run tryouts in late May and June. NY Red Bulls Academy, NYCFC and BW Gottschee run year-round MLS Next ID sessions." },
      { question: "Best ECNL clubs in New York?", answer: "Albertson SC, World Class FC, FC Westchester and Manhattan SC are among the most consistently top-recruited ECNL programs in NY." },
      { question: "How do I join NY Red Bulls Academy?", answer: "NYRB Academy recruits through MLS Next play and regional ID camps. Open tryouts are limited — most Academy players come through scouted competition. NYRB has one of the strongest MLS homegrown pipelines in the league." },
      { question: "What's the difference between ENY and Western NY State Soccer?", answer: "Eastern NY (ENY) covers NYC, Long Island and the Hudson Valley. Western and Central NY State Soccer cover Buffalo, Rochester and Syracuse. Each runs its own State Cup." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "north-dakota-youth-soccer": {
    state: "North Dakota",
    abbreviation: "ND",
    heroTagline:
      "Talking Soccer ND — North Dakota youth soccer: Fargo United, Bismarck SC and the Fargo–Bismarck–Grand Forks club talk.",
    leagues: [
      { name: "North Dakota Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Midwest Regionals." },
      { name: "Dakota Alliance Soccer Conference (DASC)", description: "Top regional travel league connecting ND with SD and western MN clubs." },
      { name: "North Dakota Premier League", description: "Top in-state travel league for U13–U19." },
    ],
    tournaments: [
      { name: "Schwan's USA Cup crossover (MN)", description: "Many top ND clubs travel to Blaine, MN for Schwan's USA Cup each summer." },
      { name: "Fargo Soccer Tournament", description: "Long-running Fargo-area showcase." },
    ],
    tryoutWindows: "ND travel clubs run tryouts in late May–June; short outdoor season pushes some clubs to indoor competitive in winter.",
    collegePipeline: "North Dakota players regularly commit to North Dakota, North Dakota State, Minnesota and a steady flow of Summit League programs.",
    faqs: [
      { question: "When are North Dakota soccer tryouts?", answer: "Most ND travel clubs hold tryouts in late May and June." },
      { question: "Best youth soccer clubs in North Dakota?", answer: "Fargo United and Bismarck SC are the most active competitive programs in North Dakota." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "oklahoma-youth-soccer": {
    state: "Oklahoma",
    abbreviation: "OK",
    heroTagline:
      "Talking Soccer OK — Oklahoma youth soccer: Tulsa Soccer Club, OK Energy FC, Oklahoma City Energy, OPSC and the OKC–Tulsa club scene.",
    leagues: [
      { name: "Oklahoma Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Southern Regionals." },
      { name: "ECNL Regional League — South Central", description: "Tulsa SC, OK Energy FC and Oklahoma City Energy field ECNL Regional age groups." },
      { name: "MLS Next — OK Energy FC", description: "Oklahoma's flagship boys' MLS Next pathway." },
      { name: "Oklahoma Premier League", description: "Top in-state travel league for U13–U19." },
    ],
    tournaments: [
      { name: "Tulsa Nationals", description: "Long-running Tulsa recruiting event drawing South Central clubs." },
      { name: "OKC Energy Cup", description: "Major Oklahoma City showcase with strong MLS Next field." },
    ],
    tryoutWindows: "OK travel and ECNL clubs run tryouts in late May–June. OK Energy FC runs year-round MLS Next ID.",
    collegePipeline: "Oklahoma players regularly commit to Oklahoma State, Oklahoma, Tulsa and a steady flow of Big 12 and AAC programs.",
    faqs: [
      { question: "When are Oklahoma soccer tryouts?", answer: "Most Oklahoma travel and ECNL clubs hold tryouts in late May and June." },
      { question: "Best youth soccer clubs in Oklahoma?", answer: "OK Energy FC, Tulsa Soccer Club and Oklahoma City Energy are the most consistently top-recruited clubs in Oklahoma." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "rhode-island-youth-soccer": {
    state: "Rhode Island",
    abbreviation: "RI",
    heroTagline:
      "Talking Soccer RI — Rhode Island youth soccer: Bayside FC, Ocean State Soccer Club, Bolts FC and the Providence and Newport-area club scene.",
    leagues: [
      { name: "Rhode Island Youth Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Eastern Regionals." },
      { name: "NEP (New England Premiership)", description: "Top regional travel league connecting RI with MA, NH, ME and CT clubs." },
      { name: "ECNL Regional League — New England", description: "Bayside FC and other top RI clubs field ECNL Regional age groups." },
    ],
    tournaments: [
      { name: "Bayside Memorial Day", description: "Long-running RI recruiting event drawing New England clubs." },
      { name: "Newport Cup", description: "Coastal RI showcase popular with regional travel clubs." },
    ],
    tryoutWindows: "RI travel and ECNL clubs run tryouts in late May–June for the following season.",
    collegePipeline: "Rhode Island players regularly commit to URI, Brown, Providence, Bryant and a steady flow of Patriot League and NEC programs.",
    faqs: [
      { question: "When are Rhode Island soccer tryouts?", answer: "Most RI travel and ECNL clubs hold tryouts in late May and June." },
      { question: "Best youth soccer clubs in Rhode Island?", answer: "Bayside FC, Ocean State Soccer Club and Bolts FC are the most consistently top-recruited clubs in Rhode Island." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "south-carolina-youth-soccer": {
    state: "South Carolina",
    abbreviation: "SC",
    heroTagline:
      "Talking Soccer SC — South Carolina youth soccer: SCUFC, CESA, Charleston Battery Academy, South Carolina United and the Greenville–Charleston–Columbia club scene.",
    leagues: [
      { name: "South Carolina Youth Soccer & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Southern Regionals." },
      { name: "ECNL Carolinas — South Carolina", description: "CESA, SCUFC and Charleston Battery Academy anchor the ECNL pathway in-state." },
      { name: "MLS Next — Charleston Battery & SCUFC", description: "South Carolina's top boys' MLS Next pathways." },
      { name: "South Carolina Premier League", description: "Top in-state travel league for U13–U19." },
    ],
    tournaments: [
      { name: "CESA Showcase (Greenville)", description: "Major Southeast recruiting event drawing national clubs." },
      { name: "Charleston Battery Cup", description: "Hosted by Charleston Battery Academy with strong MLS Next field." },
    ],
    tryoutWindows: "SC travel and ECNL clubs run tryouts in late May–June. Charleston Battery Academy runs year-round MLS Next ID.",
    collegePipeline: "South Carolina players regularly commit to South Carolina, Clemson, Furman, Wofford and a wide range of ACC and Southern Conference programs.",
    faqs: [
      { question: "When are South Carolina soccer tryouts?", answer: "Most SC travel and ECNL clubs hold tryouts in late May and June." },
      { question: "Best youth soccer clubs in South Carolina?", answer: "CESA, SCUFC, Charleston Battery Academy and South Carolina United are the most consistently top-recruited clubs in South Carolina." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "south-dakota-youth-soccer": {
    state: "South Dakota",
    abbreviation: "SD",
    heroTagline:
      "Talking Soccer SD — South Dakota youth soccer: Dakota Alliance, Sioux Falls SC, Rapid City and the Sioux Falls–Rapid City club talk.",
    leagues: [
      { name: "South Dakota Youth Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Midwest Regionals." },
      { name: "Dakota Alliance Soccer Conference (DASC)", description: "Top regional travel league connecting SD with ND and western MN clubs." },
      { name: "South Dakota Premier League", description: "Top in-state travel league for U13–U19." },
    ],
    tournaments: [
      { name: "Schwan's USA Cup crossover (MN)", description: "Many top SD clubs travel to Blaine, MN for Schwan's USA Cup each summer." },
      { name: "Sioux Falls Soccer Showcase", description: "Long-running SD recruiting event." },
    ],
    tryoutWindows: "SD travel clubs run tryouts in late May–June for the following season.",
    collegePipeline: "South Dakota players regularly commit to South Dakota, South Dakota State, Augustana and a steady flow of Summit League and NSIC programs.",
    faqs: [
      { question: "When are South Dakota soccer tryouts?", answer: "Most SD travel clubs hold tryouts in late May and June." },
      { question: "Best youth soccer clubs in South Dakota?", answer: "Dakota Alliance and Sioux Falls SC are the most active competitive programs in South Dakota." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "tennessee-youth-soccer": {
    state: "Tennessee",
    abbreviation: "TN",
    heroTagline:
      "Talking Soccer TN — Tennessee youth soccer: Tennessee SC, Nashville SC Academy, CFC Nashville, Memphis Soccer Club and the Nashville–Memphis–Knoxville club scene.",
    leagues: [
      { name: "Tennessee State Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Southern Regionals." },
      { name: "ECNL Southeast — Tennessee", description: "Tennessee SC, CFC Nashville and Memphis SC anchor the ECNL pathway." },
      { name: "MLS Next — Nashville SC Academy", description: "Tennessee's flagship boys' MLS Next pathway." },
      { name: "Tennessee Premier League", description: "Top in-state travel league for U13–U19." },
    ],
    tournaments: [
      { name: "Music City Showcase (Nashville)", description: "Major Southeast recruiting event drawing national clubs." },
      { name: "Nashville SC Cup", description: "Hosted by Nashville SC Academy with strong MLS Next field." },
    ],
    tryoutWindows: "TN travel and ECNL clubs run tryouts in late May–June. Nashville SC Academy runs year-round MLS Next ID.",
    collegePipeline: "Tennessee players regularly commit to Tennessee, Vanderbilt, Memphis, Lipscomb and a wide range of SEC and ACC programs.",
    faqs: [
      { question: "When are Tennessee soccer tryouts?", answer: "Most Tennessee travel and ECNL clubs hold tryouts in late May and June. Nashville SC Academy runs year-round MLS Next ID sessions." },
      { question: "Best youth soccer clubs in Tennessee?", answer: "Tennessee SC, Nashville SC Academy, CFC Nashville and Memphis SC are the most consistently top-recruited clubs in Tennessee." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "utah-youth-soccer": {
    state: "Utah",
    abbreviation: "UT",
    heroTagline:
      "Talking Soccer UT — Utah youth soccer: Real Salt Lake Academy, La Roca, Utah Avalanche, Celtic FC and the Salt Lake–Utah Valley club scene.",
    leagues: [
      { name: "Utah Youth Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Far West Regionals." },
      { name: "ECNL Mountain — Utah", description: "La Roca, Celtic FC and Utah Avalanche anchor the ECNL pathway." },
      { name: "MLS Next — Real Salt Lake Academy", description: "One of the top boys' MLS Next pathways in the country, anchored at the Wasatch Academy in Herriman." },
      { name: "Utah Premier League", description: "Top in-state travel league for U13–U19." },
    ],
    tournaments: [
      { name: "RSL Cup", description: "Hosted by Real Salt Lake Academy with strong MLS Next and ECNL field." },
      { name: "La Roca Cup", description: "Long-running Utah showcase drawing Mountain region clubs." },
      { name: "Utah Surf Cup", description: "Major regional showcase in the Salt Lake metro." },
    ],
    tryoutWindows: "UT travel and ECNL clubs run tryouts in late May–June. RSL Academy runs year-round MLS Next ID.",
    collegePipeline: "Utah produces a steady stream of D1 commits and MLS homegrowns. RSL Academy has one of the strongest pro pipelines in the league; players also feed BYU, Utah Valley, Weber State and a wide range of WCC and Pac-12 programs.",
    faqs: [
      { question: "When are Utah soccer tryouts?", answer: "Most Utah travel and ECNL clubs run tryouts in late May and June. RSL Academy runs year-round MLS Next ID sessions." },
      { question: "Best youth soccer clubs in Utah?", answer: "Real Salt Lake Academy, La Roca, Celtic FC and Utah Avalanche are the most consistently top-recruited clubs in Utah." },
      { question: "How do I join RSL Academy?", answer: "RSL Academy recruits through MLS Next play and regional ID camps. The Academy is residential at the Wasatch Academy in Herriman and has one of the strongest MLS homegrown pipelines in the league." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "vermont-youth-soccer": {
    state: "Vermont",
    abbreviation: "VT",
    heroTagline:
      "Talking Soccer VT — Vermont youth soccer: Nordic Soccer Club, FC Stars Vermont, Champlain United and the Burlington-area club talk.",
    leagues: [
      { name: "Vermont Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Eastern Regionals." },
      { name: "NEP (New England Premiership)", description: "Top regional travel league connecting VT with MA, NH, ME, RI and CT clubs." },
      { name: "Vermont Premier League", description: "Top in-state travel league for U13–U19." },
    ],
    tournaments: [
      { name: "Burlington Soccer Tournament", description: "Long-running VT recruiting event drawing New England clubs." },
      { name: "NEP showcases", description: "Top VT teams travel to NEP showcase weekends across New England." },
    ],
    tryoutWindows: "VT travel clubs run tryouts in late May–June for the following season.",
    collegePipeline: "Vermont players regularly commit to Vermont, Middlebury, UVM, Saint Michael's and a steady flow of NESCAC and America East programs.",
    faqs: [
      { question: "When are Vermont soccer tryouts?", answer: "Most VT travel clubs hold tryouts in late May and June." },
      { question: "Best youth soccer clubs in Vermont?", answer: "Nordic Soccer Club, FC Stars Vermont and Champlain United are the most active competitive programs in Vermont." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "virginia-youth-soccer": {
    state: "Virginia",
    abbreviation: "VA",
    heroTagline:
      "Talking Soccer VA — Virginia youth soccer: Arlington Soccer, Loudoun Soccer, McLean YS, Richmond Strikers, Virginia Beach City and the NoVA–Richmond–Tidewater club scene.",
    leagues: [
      { name: "Virginia Youth Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Eastern Regionals." },
      { name: "ECNL Mid-Atlantic — Virginia", description: "Arlington Soccer, Loudoun Soccer, McLean YS and Virginia Beach City anchor the ECNL pathway." },
      { name: "MLS Next — Virginia clubs", description: "Loudoun United Academy, Richmond Strikers and Virginia Beach City field strong MLS Next age groups." },
      { name: "NCSL (National Capital Soccer League) & ODSL", description: "Anchor weekly travel competition across NoVA and the rest of the state." },
    ],
    tournaments: [
      { name: "Jefferson Cup (Richmond)", description: "One of the largest and most college-attended showcases on the East Coast." },
      { name: "Bethesda Premier Cup", description: "Major Mid-Atlantic showcase with strong national draw." },
      { name: "Richmond Strikers Showcase", description: "Long-running RVA recruiting event." },
    ],
    tryoutWindows: "VA travel and ECNL clubs run tryouts in late May–June. MLS Next ID runs year-round.",
    collegePipeline: "Virginia is one of the deepest pipelines in the country — feeding UVA (a longtime men's power), William & Mary, Virginia Tech, JMU, Old Dominion and a wide range of ACC and CAA programs.",
    faqs: [
      { question: "When are Virginia soccer tryouts?", answer: "Most Virginia travel and ECNL clubs run tryouts in late May and June. MLS Next clubs run year-round ID sessions." },
      { question: "Best ECNL clubs in Virginia?", answer: "Arlington Soccer, Loudoun Soccer, McLean YS, Richmond Strikers and Virginia Beach City are among the most consistently top-recruited ECNL programs in Virginia." },
      { question: "How big is Jefferson Cup?", answer: "Jefferson Cup in Richmond is one of the largest and most college-attended youth soccer showcases on the East Coast every March — most top East Coast clubs target it." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "west-virginia-youth-soccer": {
    state: "West Virginia",
    abbreviation: "WV",
    heroTagline:
      "Talking Soccer WV — West Virginia youth soccer: WV Futbol Club, Mountaineer SA, West Virginia United and the Charleston–Morgantown–Huntington club talk.",
    leagues: [
      { name: "West Virginia Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Eastern Regionals." },
      { name: "ECNL Regional League — Mid-Atlantic", description: "WV Futbol Club and West Virginia United field ECNL Regional age groups." },
      { name: "West Virginia Premier League", description: "Top in-state travel league for U13–U19." },
    ],
    tournaments: [
      { name: "Mountaineer Cup (Morgantown)", description: "Long-running WV recruiting event." },
      { name: "Charleston Cup", description: "Capital-area showcase popular with regional clubs." },
    ],
    tryoutWindows: "WV travel clubs run tryouts in late May–June for the following season.",
    collegePipeline: "West Virginia players regularly commit to WVU, Marshall, Charleston and a steady flow of Big 12 and Conference USA programs.",
    faqs: [
      { question: "When are West Virginia soccer tryouts?", answer: "Most WV travel clubs hold tryouts in late May and June." },
      { question: "Best youth soccer clubs in West Virginia?", answer: "WV Futbol Club, Mountaineer SA and West Virginia United are the most active competitive programs in West Virginia." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "wisconsin-youth-soccer": {
    state: "Wisconsin",
    abbreviation: "WI",
    heroTagline:
      "Talking Soccer WI — Wisconsin youth soccer: FC Milwaukee Nationals, Bavarian SC, FC Wisconsin, SC Waukesha and the Milwaukee–Madison club scene.",
    leagues: [
      { name: "Wisconsin Youth Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Midwest Regionals." },
      { name: "ECNL Great Lakes — Wisconsin", description: "FC Milwaukee Nationals, Bavarian SC and FC Wisconsin anchor the ECNL pathway." },
      { name: "MLS Next — Wisconsin clubs", description: "FC Milwaukee Nationals and FC Wisconsin field strong MLS Next age groups." },
      { name: "MRL (Midwest Regional League)", description: "Top regional travel league connecting WI with IL, MI and OH." },
    ],
    tournaments: [
      { name: "FC Wisconsin Nationals Cup", description: "Major Wisconsin recruiting event drawing Midwest clubs." },
      { name: "Schwan's USA Cup crossover (MN)", description: "Many top WI clubs travel to Blaine, MN for Schwan's USA Cup each summer." },
    ],
    tryoutWindows: "WI travel and ECNL clubs run tryouts in late May–June for the following season.",
    collegePipeline: "Wisconsin players regularly commit to Wisconsin, Marquette, UW–Milwaukee and a wide range of Big Ten and Big East programs.",
    faqs: [
      { question: "When are Wisconsin soccer tryouts?", answer: "Most Wisconsin travel and ECNL clubs hold tryouts in late May and June." },
      { question: "Best youth soccer clubs in Wisconsin?", answer: "FC Milwaukee Nationals, Bavarian SC, FC Wisconsin and SC Waukesha are the most consistently top-recruited clubs in Wisconsin." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "ECNL & MLS Next", to: "/ecnl-and-mls-next" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "wyoming-youth-soccer": {
    state: "Wyoming",
    abbreviation: "WY",
    heroTagline:
      "Talking Soccer WY — Wyoming youth soccer: Cheyenne, Casper, Jackson Hole and the Wyoming club talk.",
    leagues: [
      { name: "Wyoming Soccer Association & State Cup", description: "Governing body and State Cup pathway to US Youth Soccer Far West Regionals." },
      { name: "Wyoming Premier League", description: "Top in-state travel league for U13–U19." },
      { name: "Mountain Region crossover", description: "Top WY teams often travel into Colorado or Utah for stronger weekly competition." },
    ],
    tournaments: [
      { name: "Wyoming State Cup", description: "Annual statewide championship across age groups." },
      { name: "Jackson Hole Soccer Tournament", description: "Iconic mountain-town summer event." },
    ],
    tryoutWindows: "WY travel clubs run tryouts in late May–June; short outdoor season pushes some clubs to indoor competitive in winter.",
    collegePipeline: "Wyoming players regularly commit to Wyoming and a steady flow of Mountain West, RMAC and NAIA programs.",
    faqs: [
      { question: "When are Wyoming soccer tryouts?", answer: "Most Wyoming travel clubs hold tryouts in late May and June." },
      { question: "Best youth soccer clubs in Wyoming?", answer: "Cheyenne, Casper and Jackson Hole-area programs anchor the competitive youth soccer scene in Wyoming." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "alberta-youth-soccer": {
    state: "Alberta",
    abbreviation: "AB",
    heroTagline:
      "Talking Soccer AB — Alberta youth soccer: Cavalry FC Pre-Academy, Calgary Foothills, Edmonton Scottish, St. Albert Impact and the Calgary–Edmonton club scene.",
    leagues: [
      { name: "Alberta Soccer Association & Provincial Cup", description: "Governing body and Provincial Cup feeding Canada Soccer National Championships." },
      { name: "Alberta Major Soccer League (AMSL)", description: "Top provincial travel league for boys and girls youth competition." },
      { name: "MLS Next — Alberta crossover", description: "Top AB boys' players often pursue MLS Next pathways via US-side academies; Cavalry FC runs a Pre-Academy pathway in-province." },
      { name: "BC Soccer Premier League crossover", description: "Top AB clubs occasionally play into BCSPL for stronger weekly competition." },
    ],
    tournaments: [
      { name: "Calgary Foothills Soccer Tournament", description: "Long-running Calgary showcase drawing Western Canadian clubs." },
      { name: "Edmonton Soccer Tournament", description: "Top Alberta capital-area event each summer." },
    ],
    tryoutWindows: "AB youth clubs run tryouts in spring (April–May) for the summer outdoor season; indoor leagues run fall through winter.",
    collegePipeline: "Alberta players regularly commit to U Sports programs (Alberta, Calgary, MacEwan, Mount Royal) and a steady flow of US NCAA D1/D2 programs.",
    faqs: [
      { question: "When are Alberta soccer tryouts?", answer: "Most Alberta youth clubs hold tryouts in spring — typically April and May — for the summer outdoor season. Indoor leagues run fall and winter." },
      { question: "Best youth soccer clubs in Alberta?", answer: "Calgary Foothills, Cavalry FC Pre-Academy, Edmonton Scottish and St. Albert Impact are among the most consistently top-recruited clubs in Alberta." },
      { question: "How do AB players get to MLS Next or NCAA?", answer: "Top Alberta players typically pursue MLS Next via US-side academies, with Cavalry FC's Pre-Academy as the main in-province pathway. U Sports and NCAA D1 are both common college destinations." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "british-columbia-youth-soccer": {
    state: "British Columbia",
    abbreviation: "BC",
    heroTagline:
      "Talking Soccer BC — British Columbia youth soccer: Whitecaps FC Academy, Vancouver FC, Coastal FC, TSS Rovers, Fraser Valley Soccer and the Greater Vancouver–Victoria club scene.",
    leagues: [
      { name: "BC Soccer & Provincial Cup", description: "Governing body and Provincial A/B Cup feeding Canada Soccer National Championships." },
      { name: "BC Soccer Premier League (BCSPL)", description: "Top provincial league for elite youth boys and girls — the proving ground for the Whitecaps Academy pathway." },
      { name: "MLS Next — Vancouver Whitecaps Academy", description: "BC's flagship boys' pathway competing in MLS Next." },
      { name: "Coastal Cup & VMSL pathway", description: "Top BC senior pathways via League1 BC for older youth and emerging pros." },
    ],
    tournaments: [
      { name: "Coastal Classic", description: "Long-running Vancouver-area showcase drawing Pacific Northwest clubs." },
      { name: "Surrey United Showcase", description: "Major Fraser Valley recruiting event." },
      { name: "Canada Soccer Nationals", description: "BCSPL champions feed into Canada Soccer National Championships each summer." },
    ],
    tryoutWindows: "BCSPL clubs typically run identification sessions in February–March with tryouts in April–May. Outdoor season runs roughly April through October.",
    collegePipeline: "British Columbia produces a deep stream of U Sports and NCAA D1 commits. Whitecaps Academy regularly produces MLS homegrowns; top players also feed UBC, UVic, TWU, SFU and US programs across the Pac-12, WCC and beyond.",
    faqs: [
      { question: "When are BC soccer tryouts?", answer: "BCSPL clubs typically run ID sessions in February and March, with tryouts in April and May for the summer outdoor season." },
      { question: "What is BCSPL?", answer: "The BC Soccer Premier League (BCSPL) is the top provincial youth league in British Columbia for boys and girls — the proving ground for the Whitecaps Academy and the strongest weekly competition in the province." },
      { question: "How do I join Whitecaps Academy?", answer: "Whitecaps FC Academy recruits through BCSPL play and regional ID camps. Open tryouts are limited — most Academy players come through scouted BCSPL competition." },
      { question: "Best youth soccer clubs in BC?", answer: "Whitecaps FC Academy, Coastal FC, Fusion FC, Surrey United, Mountain United and TSS FC are among the most consistently top-recruited clubs in British Columbia." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "manitoba-youth-soccer": {
    state: "Manitoba",
    abbreviation: "MB",
    heroTagline:
      "Talking Soccer MB — Manitoba youth soccer: WSA Winnipeg, Bonivital, FC Northwest, Valour FC pathway and Winnipeg-area club talk.",
    leagues: [
      { name: "Manitoba Soccer Association & Provincial Cup", description: "Governing body and Provincial Cup feeding Canada Soccer National Championships." },
      { name: "Manitoba Major Soccer League (MMSL) youth pathway", description: "Top in-province pathway for older youth and adult competition." },
      { name: "Winnipeg Youth Soccer Association (WYSA)", description: "Largest youth soccer body in Manitoba — anchors weekly play across age groups." },
    ],
    tournaments: [
      { name: "Manitoba Provincial Cup", description: "Annual provincial championship with Canada Soccer Nationals pathway." },
      { name: "Winnipeg Cup", description: "Long-running summer showcase drawing Prairie clubs." },
    ],
    tryoutWindows: "MB youth clubs run tryouts in spring (April–May) for the summer outdoor season; indoor leagues run fall and winter.",
    collegePipeline: "Manitoba players regularly commit to U Sports programs (Manitoba, Winnipeg) and a steady flow of US NCAA D2 and NAIA programs.",
    faqs: [
      { question: "When are Manitoba soccer tryouts?", answer: "Most Manitoba youth clubs hold tryouts in spring (April–May) for the summer outdoor season. Indoor leagues run fall and winter." },
      { question: "Best youth soccer clubs in Manitoba?", answer: "WSA Winnipeg, Bonivital and FC Northwest are among the most active competitive programs in Manitoba." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "new-brunswick-youth-soccer": {
    state: "New Brunswick",
    abbreviation: "NB",
    heroTagline:
      "Talking Soccer NB — New Brunswick youth soccer: Fredericton, Moncton, Saint John and Atlantic Canada club talk.",
    leagues: [
      { name: "Soccer New Brunswick & Provincial Cup", description: "Governing body and Provincial Cup feeding Canada Soccer National Championships." },
      { name: "New Brunswick Premier Soccer League", description: "Top provincial pathway for older youth competition." },
      { name: "Atlantic Premier League (APL) crossover", description: "Top NB clubs play APL for stronger regional competition with NS, PEI and NL clubs." },
    ],
    tournaments: [
      { name: "Atlantic Cup", description: "Regional showcase drawing Atlantic Canada clubs." },
      { name: "NB Provincial Cup", description: "Annual provincial championship with Canada Soccer Nationals pathway." },
    ],
    tryoutWindows: "NB youth clubs run tryouts in spring (April–May) for the summer outdoor season; indoor leagues run fall and winter.",
    collegePipeline: "NB players regularly commit to U Sports programs (UNB, Saint Mary's, UPEI) and a steady flow of US NCAA D2 programs.",
    faqs: [
      { question: "When are New Brunswick soccer tryouts?", answer: "Most NB youth clubs hold tryouts in spring (April–May) for the summer outdoor season." },
      { question: "Best youth soccer clubs in New Brunswick?", answer: "Fredericton, Moncton and Saint John area programs anchor the competitive youth soccer scene in New Brunswick." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "newfoundland-and-labrador-youth-soccer": {
    state: "Newfoundland and Labrador",
    abbreviation: "NL",
    heroTagline:
      "Talking Soccer NL — Newfoundland and Labrador youth soccer: St. John's, Mount Pearl, Holy Cross, Feildians and Atlantic Canada club talk.",
    leagues: [
      { name: "Soccer NL & Provincial Cup", description: "Governing body and Provincial Cup feeding Canada Soccer National Championships." },
      { name: "Challenge Cup", description: "Top in-province competition for elite youth and adult sides." },
      { name: "Atlantic Premier League (APL) crossover", description: "Top NL clubs occasionally play into APL for stronger regional competition." },
    ],
    tournaments: [
      { name: "Provincial Cup", description: "Annual provincial championship with Canada Soccer Nationals pathway." },
      { name: "St. John's Soccer Tournament", description: "Long-running provincial showcase." },
    ],
    tryoutWindows: "NL youth clubs run tryouts in spring (April–May) for the summer outdoor season; indoor leagues run fall and winter.",
    collegePipeline: "NL players regularly commit to U Sports programs (Memorial, Cape Breton) and occasional US NCAA D2 programs.",
    faqs: [
      { question: "When are Newfoundland and Labrador soccer tryouts?", answer: "Most NL youth clubs hold tryouts in spring (April–May) for the summer outdoor season." },
      { question: "Best youth soccer clubs in NL?", answer: "St. John's, Mount Pearl, Holy Cross and Feildians anchor the competitive youth soccer scene in Newfoundland and Labrador." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "northwest-territories-youth-soccer": {
    state: "Northwest Territories",
    abbreviation: "NT",
    heroTagline:
      "Talking Soccer NT — Northwest Territories youth soccer: Yellowknife and territorial club, indoor and travel talk.",
    leagues: [
      { name: "Soccer NWT & Territorial Championships", description: "Governing body and territorial championship pathway." },
      { name: "Yellowknife Soccer Association", description: "Largest youth soccer body in the territory — anchors competitive play." },
    ],
    tournaments: [
      { name: "Arctic Winter Games (when held)", description: "Major multi-sport event featuring NT youth soccer teams." },
      { name: "Provincial crossover travel", description: "Top NT teams travel to AB or BC for tougher competition each year." },
    ],
    tryoutWindows: "Short outdoor season pushes NT tryouts into late spring; many programs run indoor through the long winter.",
    collegePipeline: "NT players occasionally commit to U Sports and US NAIA/D2 programs via Western Canadian pipelines.",
    faqs: [
      { question: "When are Northwest Territories soccer tryouts?", answer: "Most NT youth clubs hold tryouts in late spring given the short outdoor season; many programs run indoor through the long winter." },
      { question: "How do NT players compete with the rest of Canada?", answer: "Top NT teams travel to Alberta or BC for tougher competition each year and represent the territory at Canada Soccer events when invited." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "nova-scotia-youth-soccer": {
    state: "Nova Scotia",
    abbreviation: "NS",
    heroTagline:
      "Talking Soccer NS — Nova Scotia youth soccer: Halifax City, Suburban FC, Dunbrack, Halifax Wanderers pathway and the HRM and Cape Breton club scene.",
    leagues: [
      { name: "Soccer Nova Scotia & Provincial Cup", description: "Governing body and Provincial Cup feeding Canada Soccer National Championships." },
      { name: "Nova Scotia Soccer League (NSSL) youth pathway", description: "Top in-province pathway for older youth and adult competition." },
      { name: "Atlantic Premier League (APL)", description: "Top regional travel league connecting NS with NB, PEI and NL clubs." },
    ],
    tournaments: [
      { name: "Atlantic Cup", description: "Regional showcase drawing Atlantic Canada clubs." },
      { name: "Halifax City Soccer Tournament", description: "Long-running HRM-area showcase." },
    ],
    tryoutWindows: "NS youth clubs run tryouts in spring (April–May) for the summer outdoor season; indoor leagues run fall and winter.",
    collegePipeline: "NS players regularly commit to U Sports programs (Saint Mary's, Dalhousie, Acadia, Cape Breton — a longtime men's power) and a steady flow of US NCAA D2 programs.",
    faqs: [
      { question: "When are Nova Scotia soccer tryouts?", answer: "Most NS youth clubs hold tryouts in spring (April–May) for the summer outdoor season." },
      { question: "Best youth soccer clubs in Nova Scotia?", answer: "Halifax City, Suburban FC, Dunbrack and Cape Breton-area programs anchor the competitive youth soccer scene in Nova Scotia." },
      { question: "How strong is Cape Breton men's soccer?", answer: "Cape Breton University is a longtime U Sports men's soccer power and a major destination for top Atlantic Canadian players." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "nunavut-youth-soccer": {
    state: "Nunavut",
    abbreviation: "NU",
    heroTagline:
      "Talking Soccer NU — Nunavut youth soccer: Iqaluit and territorial club, indoor and travel talk.",
    leagues: [
      { name: "Soccer Nunavut & Territorial Championships", description: "Governing body and territorial championship pathway." },
      { name: "Iqaluit Soccer Association", description: "Anchors competitive youth soccer in the territory." },
    ],
    tournaments: [
      { name: "Arctic Winter Games (when held)", description: "Major multi-sport event featuring NU youth soccer teams." },
      { name: "Southern travel tournaments", description: "Top NU teams travel to southern provinces for tougher competition each year." },
    ],
    tryoutWindows: "Extremely short outdoor season — most NU soccer is indoor year-round, with summer programming where possible.",
    collegePipeline: "NU players occasionally commit to U Sports and Canadian college programs via southern Canadian pipelines.",
    faqs: [
      { question: "When are Nunavut soccer tryouts?", answer: "Most Nunavut soccer is indoor year-round given the extremely short outdoor season. Summer programming runs where possible." },
      { question: "How does Nunavut compete with the rest of Canada?", answer: "Top NU teams travel to southern provinces for tougher competition each year and represent the territory at Canada Soccer events when invited." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "ontario-youth-soccer": {
    state: "Ontario",
    abbreviation: "ON",
    heroTagline:
      "Talking Soccer ON — Ontario youth soccer: Toronto FC Academy, Vaughan SC, North Toronto, ANB Futbol, Sigma FC, OPDL clubs and the GTA, Ottawa and Southwestern Ontario club scene.",
    leagues: [
      { name: "Ontario Soccer & Provincial Cup", description: "Governing body and Provincial Cup feeding Canada Soccer National Championships." },
      { name: "Ontario Player Development League (OPDL)", description: "Top provincial youth league sanctioned by Ontario Soccer — Canada's strongest youth standards-based league." },
      { name: "MLS Next — Toronto FC Academy", description: "Ontario's flagship boys' MLS Next pathway and one of the strongest in MLS." },
      { name: "League1 Ontario youth pathway", description: "Top senior pathway feeding into the Canadian Premier League and abroad." },
    ],
    tournaments: [
      { name: "Robbie International Soccer Tournament (Toronto)", description: "One of the largest and longest-running youth soccer tournaments in North America." },
      { name: "OPDL Championships", description: "Provincial championships for top OPDL age groups." },
      { name: "TFC Academy Cup", description: "Hosted by TFC Academy with strong MLS Next field." },
    ],
    tryoutWindows: "OPDL and top Ontario clubs run identification sessions and tryouts in February–April for the outdoor season (roughly May–October).",
    collegePipeline: "Ontario produces one of the deepest pipelines in Canada — feeding U Sports (York, Toronto, Carleton, Ottawa, McMaster) and a heavy stream of NCAA D1/D2 commits. TFC Academy regularly produces MLS homegrowns and overseas transfers.",
    faqs: [
      { question: "When are Ontario soccer tryouts?", answer: "OPDL and top Ontario clubs run ID sessions and tryouts in February through April for the outdoor season (roughly May to October)." },
      { question: "What is OPDL?", answer: "The Ontario Player Development League (OPDL) is the province's top standards-based youth league, sanctioned by Ontario Soccer. It's the proving ground for the strongest youth players in Canada outside the MLS Academies." },
      { question: "How do I join Toronto FC Academy?", answer: "TFC Academy recruits through MLS Next play, OPDL competition and regional ID camps. The Academy is one of the strongest MLS homegrown pipelines in the league." },
      { question: "Best youth soccer clubs in Ontario?", answer: "Toronto FC Academy, Vaughan SC, North Toronto Soccer Club, ANB Futbol, Sigma FC, Oakville SC and Erin Mills SC are among the most consistently top-recruited clubs in Ontario." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "prince-edward-island-youth-soccer": {
    state: "Prince Edward Island",
    abbreviation: "PE",
    heroTagline:
      "Talking Soccer PE — Prince Edward Island youth soccer: Charlottetown, Summerside, UPEI Panthers pathway and Atlantic Canada club talk.",
    leagues: [
      { name: "PEI Soccer Association & Provincial Cup", description: "Governing body and Provincial Cup feeding Canada Soccer National Championships." },
      { name: "Atlantic Premier League (APL)", description: "Top regional travel league connecting PEI with NS, NB and NL clubs." },
      { name: "PEI Youth Soccer League", description: "Anchors weekly play across age groups on the island." },
    ],
    tournaments: [
      { name: "Atlantic Cup", description: "Regional showcase drawing Atlantic Canada clubs." },
      { name: "PEI Provincial Cup", description: "Annual provincial championship with Canada Soccer Nationals pathway." },
    ],
    tryoutWindows: "PEI youth clubs run tryouts in spring (April–May) for the summer outdoor season.",
    collegePipeline: "PEI players regularly commit to UPEI, Cape Breton, Saint Mary's and a steady flow of U Sports and NCAA D2 programs.",
    faqs: [
      { question: "When are PEI soccer tryouts?", answer: "Most PEI youth clubs hold tryouts in spring (April–May) for the summer outdoor season." },
      { question: "Best youth soccer clubs in PEI?", answer: "Charlottetown and Summerside-area programs anchor the competitive youth soccer scene in PEI." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "quebec-youth-soccer": {
    state: "Quebec",
    abbreviation: "QC",
    heroTagline:
      "Talking Soccer QC — Quebec youth soccer: CF Montréal Academy, AS Blainville, CS Mont-Royal Outremont, Lakeshore SC, PLSQ youth pathway and the Montréal, Québec City and Sherbrooke club scene.",
    leagues: [
      { name: "Soccer Québec & Coupe du Québec", description: "Governing body and Provincial Cup feeding Canada Soccer National Championships." },
      { name: "Programme de soccer interrégional (PSI / AAA)", description: "Top provincial elite youth pathway across regions of Quebec." },
      { name: "MLS Next — CF Montréal Academy", description: "Quebec's flagship boys' MLS Next pathway and one of the deepest MLS homegrown pipelines." },
      { name: "Première Ligue de soccer du Québec (PLSQ)", description: "Top senior pathway feeding into the Canadian Premier League and abroad." },
    ],
    tournaments: [
      { name: "Coupe du Québec", description: "Annual provincial championship with Canada Soccer Nationals pathway." },
      { name: "CF Montréal Academy Cup", description: "Hosted by CFM Academy with strong MLS Next field." },
      { name: "Tournoi international de Montréal", description: "Long-running international youth showcase held in the Montréal metro." },
    ],
    tryoutWindows: "Top Quebec clubs and AAA/PSI programs run identification sessions in February–April for the outdoor season (roughly May–October).",
    collegePipeline: "Quebec produces a deep stream of U Sports and NCAA D1 commits, plus CEGEP-level competition (RSEQ) as a unique Québécois pathway. CF Montréal Academy regularly produces MLS homegrowns and overseas transfers.",
    faqs: [
      { question: "When are Quebec soccer tryouts?", answer: "Most top Quebec clubs run ID sessions in February through April for the outdoor season (roughly May to October)." },
      { question: "What is PSI / AAA soccer in Quebec?", answer: "The Programme de soccer interrégional (PSI / AAA) is the top provincial elite youth pathway in Quebec, running across regions of the province as a tier below CF Montréal Academy." },
      { question: "How do I join CF Montréal Academy?", answer: "CFM Academy recruits through MLS Next play, AAA/PSI competition and regional ID camps. The Academy is one of the strongest MLS homegrown pipelines in the league." },
      { question: "What is RSEQ soccer?", answer: "RSEQ is the Québécois CEGEP-level athletics association — top Quebec youth players often play RSEQ soccer at CEGEP before continuing to U Sports or NCAA." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "saskatchewan-youth-soccer": {
    state: "Saskatchewan",
    abbreviation: "SK",
    heroTagline:
      "Talking Soccer SK — Saskatchewan youth soccer: Regina, Saskatoon, Hollandia, Lakeside Lazio and Prairie club talk.",
    leagues: [
      { name: "Saskatchewan Soccer Association & Provincial Cup", description: "Governing body and Provincial Cup feeding Canada Soccer National Championships." },
      { name: "Saskatoon Soccer Centre & Regina Soccer Association", description: "Two largest youth bodies in the province — anchor weekly play." },
      { name: "Premier League Saskatchewan (PLSK)", description: "Top provincial pathway for older youth and senior competition." },
    ],
    tournaments: [
      { name: "Saskatchewan Provincial Cup", description: "Annual provincial championship with Canada Soccer Nationals pathway." },
      { name: "Prairie Cup crossover", description: "Top SK teams travel for Prairie regional events with AB and MB clubs." },
    ],
    tryoutWindows: "SK youth clubs run tryouts in spring (April–May) for the summer outdoor season; indoor leagues run fall and winter.",
    collegePipeline: "Saskatchewan players regularly commit to U Sports programs (Saskatchewan, Regina) and a steady flow of US NCAA D2 and NAIA programs.",
    faqs: [
      { question: "When are Saskatchewan soccer tryouts?", answer: "Most SK youth clubs hold tryouts in spring (April–May) for the summer outdoor season." },
      { question: "Best youth soccer clubs in Saskatchewan?", answer: "Hollandia, Lakeside Lazio, Regina and Saskatoon-area programs anchor the competitive youth soccer scene in Saskatchewan." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },

  "yukon-youth-soccer": {
    state: "Yukon",
    abbreviation: "YT",
    heroTagline:
      "Talking Soccer YT — Yukon youth soccer: Whitehorse and territorial club, indoor and travel talk.",
    leagues: [
      { name: "Soccer Yukon & Territorial Championships", description: "Governing body and territorial championship pathway." },
      { name: "Whitehorse Youth Soccer Association", description: "Largest youth soccer body in the territory — anchors competitive play." },
    ],
    tournaments: [
      { name: "Arctic Winter Games (when held)", description: "Major multi-sport event featuring YT youth soccer teams." },
      { name: "Provincial crossover travel", description: "Top YT teams travel to BC or AB for tougher competition each year." },
    ],
    tryoutWindows: "Short outdoor season pushes YT tryouts into late spring; many programs run indoor through the long winter.",
    collegePipeline: "Yukon players occasionally commit to U Sports and US NAIA/D2 programs via Western Canadian pipelines.",
    faqs: [
      { question: "When are Yukon soccer tryouts?", answer: "Most Yukon youth clubs hold tryouts in late spring given the short outdoor season; many programs run indoor through the long winter." },
      { question: "How does Yukon compete with the rest of Canada?", answer: "Top YT teams travel to BC or Alberta for tougher competition each year and represent the territory at Canada Soccer events when invited." },
    ],
    forumLinks: [
      { label: "Club & Travel Soccer", to: "/club-and-travel-soccer" },
      { label: "Recruiting & College Soccer", to: "/recruiting-and-college-soccer" },
    ],
  },
};
